// src/components/WhatIfPage.jsx
// What-If Simulator — premium paywall edition.
//
// Every widget is wrapped in <FeatureGate> using the real gate() helper
// that reads from useFeatureConfig.  Free users see live teaser content
// (blurred demo charts, partial data, gradient fades) instead of empty
// placeholders — the paywall itself looks alive and desirable.
//
// Feature-key → plan mapping lives entirely in the server response that
// useFeatureConfig fetches from GET /api/feature-config.  Change plans
// there; nothing in this file needs to change.

import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { fmt$ } from '../utils/helpers.js';
import { planCanAccess, IntelligenceGate } from './FeatureGate.jsx';
import { useFeatureConfig } from '../hooks/useFeatureConfig.js';
import '../styles/WhatIfPage.css';

// Reads a --wi-* custom property from :root so canvas drawing (which can't
// use CSS var() directly) stays in sync with the active light/dark theme.
function getThemeColor(varName, fallback) {
  if (typeof window === 'undefined' || typeof getComputedStyle !== 'function') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
  return v || fallback;
}

/* ─────────────────────────────────────────────────────────────────────────────
   Component-specific CSS (rank badges, hero KPI, intelligence card, blueprint
   teaser, diff bar, projector tease, setup rows) now lives in ./WhatIfPage.css
   so it can respond to [data-theme="light"] for light/dark mode.
───────────────────────────────────────────────────────────────────────────── */

/* ─────────────────────────────────────────────────────────────────────────────
   TEASER CONTENT
   Static demo data shown inside locked widgets so free users can see what
   they're missing.  Nothing here is real — it's purely aspirational.
───────────────────────────────────────────────────────────────────────────── */
const DEMO = {
  setups: [
    { rank:1, label:'XAUUSD · London · Breakout',   score:2.14, wr:71, n:23, color:'#3DBE73' },
    { rank:2, label:'EURUSD · NY Open · Trend',      score:1.87, wr:64, n:31, color:'#3DBE73' },
    { rank:3, label:'GBP/USD · London · Reversal',   score:1.44, wr:58, n:17, color:'#C9A84C' },
    { rank:4, label:'XAUUSD · Asian · Scalp',        score:0.91, wr:41, n:12, color:'#888' },
  ],
  heatmap: [0,0,0.1,0.2,0.5,0.8,0.9,1,0.7,0.3,-0.2,-0.5,-0.8,-0.4,-0.1,0.2,0.4,0.3,0.1,0,0,-0.1,0,0],
  heatBest:  { label:'9–10am', value:'+$847' },
  heatWorst: { label:'2–3pm',  value:'-$390' },
  sizing: {
    riskPerTrade: 250,
    avgRR: 2.3,
    projectedPnL: 4_840,
    vsActual: +1_240,
  },
  dna: { class:'MOMENTUM HUNTER', icon:'🦁', color:'#EDD67A', wr:64, avgRR:2.3, n:147 },
  milestones: [
    { label:'Monthly P&L Target',  pct:68, current:'$3,400', target:'$5,000', color:'#3DBE73' },
    { label:'Win Rate Goal',        pct:54, current:'54%',    target:'65%',    color:'#C9A84C' },
  ],
  insights: [
    { type:'blue',  icon:'🧠', title:'Pattern Detected',  val:'-$1,890', text:'You lose 73% of trades taken between 2–4pm EST. Avoiding this 2-hour window would have saved $1,890 this month alone.' },
    { type:'red',   icon:'⚠️', title:'Risk Warning',      val:'3.1×',    text:'When you exceed 3 trades per day your avg loss is 3.1× larger. A hard daily limit of 3 would save $920/month.' },
    { type:'gold',  icon:'💡', title:'Hidden Edge Found',  val:'+$2,340', text:'Your London session trades outperform your NY trades by 2.3× on win rate. Doubling down on London adds an estimated $2,340/month.' },
  ],
};

/* ─────────────────────────────────────────────────────────────────────────────
   SMALL REUSABLES
───────────────────────────────────────────────────────────────────────────── */
function Panel({ children, style = {} }) {
  return (
    <div
      className="wi-x1"
      style={{
        ...style
      }}>
      {children}
    </div>
  );
}

function SecLabel({ children }) {
  return (
    <div className="txt-micro wi-x2">
      {children}
    </div>
  );
}

function Toggle({ on, onChange, label, sub, color = 'var(--gold)', disabled }) {
  const [focused, setFocused] = useState(false);
  const handleKey = e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChange(!on); } };
  return (
    <div
      onClick={() => !disabled && onChange(!on)}
      onKeyDown={handleKey}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      role="switch"
      aria-checked={on}
      tabIndex={disabled ? -1 : 0}
      className="wi-x3"
      style={{
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? .4 : 1,
        outline: focused ? '2px solid var(--wi-gold-600)' : 'none'
      }}>
      <div
        className="wi-x4"
        style={{
          background: on ? color : 'var(--wi-surface-100)'
        }}>
        <div
          className="wi-x5"
          style={{
            left: on ? 19 : 3
          }} />
      </div>
      <div>
        <div className="txt-body-sm fw-semibold wi-x6" style={{
          color: on ? 'var(--wi-text-850)' : 'var(--wi-text-450)'
        }}>{label}</div>
        {sub && <div className="txt-caption wi-x7" style={{
          color: on ? 'var(--wi-text-500)' : 'var(--wi-text-300)'
        }}>{sub}</div>}
      </div>
    </div>
  );
}

function Slider({ label, value, min, max, step = 1, format, onChange, disabled, color = 'var(--gold)' }) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <div
      className="wi-x8"
      style={{
        opacity: disabled ? .35 : 1
      }}>
      <div className="wi-x9">
        <span className="txt-caption">{label}</span>
        <span className="num-sm wi-x10" style={{
          color
        }}>
          {format ? format(value) : value}
        </span>
      </div>
      <div className="wi-x11">
        <div className="wi-x12"/>
        <div
          className="wi-x13"
          style={{
            width:`${pct}%`,
            background:color
          }} />
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          disabled={disabled}
          onChange={e => onChange(Number(e.target.value))}
          className="wi-x14"
          style={{
            cursor:disabled?'default':'pointer'
          }} />
        <div
          className="wi-x15"
          style={{
            left:`${pct}%`,
            background:color,
            boxShadow:`0 0 8px ${color}44`
          }} />
      </div>
    </div>
  );
}

function InfoIcon({ onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={e => { e.stopPropagation(); onClick(); }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      title="About this widget"
      className="wi-x16"
      style={{
        background: hov ? 'var(--wi-gold-150)' : 'var(--wi-surface-50)',
        border: `1px solid ${hov ? 'var(--wi-gold-350)' : 'var(--wi-surface-80)'}`,
        color: hov ? 'var(--gold)' : 'var(--wi-text-450)'
      }}>i
          </button>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   INFO PANEL (slide-in)
───────────────────────────────────────────────────────────────────────────── */
function InfoPanel({ open, onClose, title, icon, children }) {
  useEffect(() => {
    if (!open) return;
    const handler = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);
  return (
    <>
      <div
        onClick={onClose}
        className="wi-x17"
        style={{
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none'
        }} />
      <div
        className="wi-x18"
        style={{
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          boxShadow: open ? '-12px 0 48px var(--wi-shadow-panel)' : 'none'
        }}>
        <div className="wi-x19">
          <div className="wi-x20">{icon}</div>
          <div className="wi-x21">
            <div className="txt-micro wi-x22">WIDGET INFO</div>
            <div className="h5 wi-x23">{title}</div>
          </div>
          <button onClick={onClose} className="wi-x24">✕</button>
        </div>
        <div className="wi-x25">{children}</div>
      </div>
    </>
  );
}
function InfoSection({ heading, children }) {
  return (
    <div className="wi-x26">
      <div className="txt-micro wi-x27">{heading}</div>
      <div className="txt-body-sm wi-x28">{children}</div>
    </div>
  );
}
function InfoTip({ children }) {
  return (
    <div className="wi-x29">
      <div className="txt-caption wi-x30">💡 {children}</div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   EQUITY CANVAS
───────────────────────────────────────────────────────────────────────────── */
function EquityCanvas({ actual, hypothetical }) {
  const cvRef = useRef(null);
  const draw = useCallback(() => {
    const cv = cvRef.current; if (!cv) return;
    const dpr = window.devicePixelRatio || 1;
    const W = cv.parentElement.offsetWidth || 600, H = 200;
    cv.width = W * dpr; cv.height = H * dpr;
    cv.style.width = W + 'px'; cv.style.height = H + 'px';
    const ctx = cv.getContext('2d'); ctx.scale(dpr, dpr); ctx.clearRect(0, 0, W, H);
    const allVals = [...actual, ...hypothetical];
    if (allVals.length < 2) return;
    const mn = allVals.reduce((a, v) => Math.min(a, v), 0);
    const mx = allVals.reduce((a, v) => Math.max(a, v), 0.01);
    const range = mx - mn || 1;
    const pad = { l:52, r:12, t:12, b:28 };
    const xs = (i, len) => pad.l + (i / Math.max(len - 1, 1)) * (W - pad.l - pad.r);
    const ys = v => H - pad.b - ((v - mn) / range) * (H - pad.t - pad.b);
    [0, 0.25, 0.5, 0.75, 1].forEach(f => {
      const v = mn + f * range;
      ctx.beginPath(); ctx.moveTo(pad.l, ys(v)); ctx.lineTo(W - pad.r, ys(v));
      ctx.strokeStyle = getThemeColor('--wi-surface-40', 'rgba(255, 255, 255, 0.04)'); ctx.lineWidth = 1; ctx.stroke();
      ctx.fillStyle = getThemeColor('--wi-text-400', 'rgba(240,234,214,0.4)'); ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'right';
      ctx.fillText(fmt$(v, false), pad.l - 5, ys(v) + 3);
    });
    if (mn < 0 && mx > 0) {
      ctx.beginPath(); ctx.moveTo(pad.l, ys(0)); ctx.lineTo(W - pad.r, ys(0));
      ctx.strokeStyle = getThemeColor('--wi-surface-120', 'rgba(255, 255, 255, 0.12)'); ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]); ctx.stroke(); ctx.setLineDash([]);
    }
    const drawCurve = (pts, color, fillColor, dash = []) => {
      if (pts.length < 2) return;
      const len = pts.length;
      ctx.setLineDash(dash);
      ctx.beginPath(); ctx.moveTo(xs(0, len), ys(pts[0]));
      pts.forEach((v, i) => { if (i > 0) ctx.lineTo(xs(i, len), ys(v)); });
      ctx.lineTo(xs(pts.length - 1, len), H - pad.b); ctx.lineTo(xs(0, len), H - pad.b); ctx.closePath();
      ctx.fillStyle = fillColor; ctx.fill();
      ctx.beginPath(); ctx.moveTo(xs(0, len), ys(pts[0]));
      pts.forEach((v, i) => { if (i > 0) ctx.lineTo(xs(i, len), ys(v)); });
      ctx.strokeStyle = color; ctx.lineWidth = 2; ctx.lineJoin = 'round'; ctx.stroke();
      ctx.setLineDash([]);
    };
    drawCurve(actual, '#D94F4F', getThemeColor('--wi-red-80', 'rgba(217,79,79,0.08)'));
    drawCurve(hypothetical, '#3DBE73', getThemeColor('--wi-green-100', 'rgba(61,190,115,0.10)'));
    [[actual, '#D94F4F'], [hypothetical, '#3DBE73']].forEach(([pts, c]) => {
      if (!pts.length) return;
      const x = xs(pts.length - 1, pts.length), y = ys(pts[pts.length - 1]);
      ctx.beginPath(); ctx.arc(x, y, 4, 0, Math.PI * 2); ctx.fillStyle = c; ctx.fill();
      ctx.beginPath(); ctx.arc(x, y, 7, 0, Math.PI * 2); ctx.strokeStyle = c + '55'; ctx.lineWidth = 1; ctx.stroke();
    });
  }, [actual, hypothetical]);
  useEffect(() => {
    draw();
    window.addEventListener('resize', draw);
    return () => window.removeEventListener('resize', draw);
  }, [draw]);
  return <canvas ref={cvRef} className="wi-x31"/>;
}

/* ─────────────────────────────────────────────────────────────────────────────
   WIDGET 1 — COMPOUND GROWTH PROJECTOR
   Real version (unlocked) + teaser version (locked)
───────────────────────────────────────────────────────────────────────────── */
function CompoundProjector({ trades, activeAccount }) {
  const initBalance = Math.max(100, parseFloat(activeAccount?.balance) || 10_000);
  const [startBalance, setStartBalance]       = useState(initBalance);
  const [startBalanceStr, setStartBalanceStr] = useState(String(initBalance));
  const [months, setMonths]                   = useState(12);
  const [infoOpen, setInfoOpen]               = useState(false);
  const sparkRef = useRef(null);

  // Sync when account switches
  useEffect(() => {
    const b = Math.max(100, parseFloat(activeAccount?.balance) || 10_000);
    setStartBalance(b);
    setStartBalanceStr(String(b));
  }, [activeAccount?.id, activeAccount?.balance]);

  const monthlyReturn = useMemo(() => {
    if (!trades.length) return 0;
    const byMonth = {};
    trades.forEach(t => {
      const key = (t.date || '').slice(0, 7);
      if (key) byMonth[key] = (byMonth[key] || 0) + Number(t.result);
    });
    const vals = Object.values(byMonth);
    if (!vals.length) return 0;
    return (vals.reduce((a, v) => a + v, 0) / vals.length) / startBalance * 100;
  }, [trades, startBalance]);

  const projection = useMemo(() => {
    const r = monthlyReturn / 100;
    const pts = [startBalance];
    for (let i = 1; i <= months; i++) pts.push(pts[pts.length - 1] * (1 + r));
    return pts;
  }, [startBalance, monthlyReturn, months]);

  const finalBalance = projection[projection.length - 1];
  const profit       = finalBalance - startBalance;
  const totalReturn  = (profit / startBalance) * 100;

  // Draw sparkline
  useEffect(() => {
    const cv = sparkRef.current; if (!cv) return;
    const dpr = window.devicePixelRatio || 1;
    const W = cv.parentElement?.offsetWidth || 300, H = 80;
    cv.width = W * dpr; cv.height = H * dpr;
    cv.style.width = W + 'px'; cv.style.height = H + 'px';
    const ctx = cv.getContext('2d'); ctx.scale(dpr, dpr); ctx.clearRect(0, 0, W, H);
    const pts = projection;
    if (pts.length < 2) return;
    const mn = Math.min(...pts), mx = Math.max(...pts), range = mx - mn || 1;
    const pad = { l:8, r:8, t:6, b:6 };
    const xs = i => pad.l + (i / (pts.length - 1)) * (W - pad.l - pad.r);
    const ys = v => H - pad.b - ((v - mn) / range) * (H - pad.t - pad.b);
    const color = totalReturn >= 0 ? '#3DBE73' : '#D94F4F';
    ctx.beginPath(); ctx.moveTo(xs(0), ys(pts[0]));
    pts.forEach((v, i) => { if (i > 0) ctx.lineTo(xs(i), ys(v)); });
    ctx.lineTo(xs(pts.length - 1), H - pad.b); ctx.lineTo(xs(0), H - pad.b); ctx.closePath();
    ctx.fillStyle = totalReturn >= 0
      ? getThemeColor('--wi-green-120', 'rgba(61,190,115,0.12)')
      : getThemeColor('--wi-red-100',   'rgba(217,79,79,0.10)');
    ctx.fill();
    ctx.beginPath(); ctx.moveTo(xs(0), ys(pts[0]));
    pts.forEach((v, i) => { if (i > 0) ctx.lineTo(xs(i), ys(v)); });
    ctx.strokeStyle = color; ctx.lineWidth = 2; ctx.lineJoin = 'round'; ctx.stroke();
  }, [projection, totalReturn]);

  const fmtB = n => n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${Math.round(n).toLocaleString()}`;

  return (
    <Panel>
      <div className="wi-x32">
        <div>
          <div className="h5 wi-x33">📈 Compound Growth Projector</div>
          <div className="txt-caption txt-muted">Project your performance forward using your actual avg monthly return</div>
        </div>
        <div className="wi-x34">
          <div className="wi-x35">
            <span className="txt-micro wi-x36">Avg Monthly</span>
            <span className="num-sm wi-x37" style={{
              color: monthlyReturn >= 0 ? 'var(--green)' : 'var(--red)'
            }}>
              {monthlyReturn >= 0 ? '+' : ''}{monthlyReturn.toFixed(2)}%
            </span>
          </div>
          <InfoIcon onClick={() => setInfoOpen(true)}/>
        </div>
      </div>
      <InfoPanel open={infoOpen} onClose={() => setInfoOpen(false)} title="Compound Growth Projector" icon="📈">
        <InfoSection heading="WHAT IT DOES">
          Takes your average monthly return — calculated from your actual logged trades — and projects it forward using compound interest.
        </InfoSection>
        <InfoSection heading="HOW TO USE IT">
          Set your starting balance to match your real account size, then drag the months slider.
        </InfoSection>
        <InfoTip>If your monthly return is negative, fix your edge in the Simulator filters first — compounding a negative return just accelerates losses.</InfoTip>
      </InfoPanel>
      <div className="wi-x38">
        <div>
          <div className="txt-micro txt-muted wi-x39">Starting Balance</div>
          <div className="wi-pip-badge">
            <span className="wi-x40">$</span>
            <input type="number" value={startBalanceStr} min={100} max={10_000_000} step={1000}
              onChange={e => { setStartBalanceStr(e.target.value); const n = Number(e.target.value); if (n >= 100) setStartBalance(n); }}
              onBlur={e => { const n = Math.max(100, Number(e.target.value) || 100); setStartBalance(n); setStartBalanceStr(String(n)); }}
              className="wi-x41"/>
          </div>
        </div>
        <div><Slider label="Projection Months" value={months} min={1} max={36} step={1} format={v => `${v}mo`} onChange={setMonths} color="var(--blue)"/></div>
      </div>
      <div className="wi-x42">
        <canvas ref={sparkRef} className="wi-x43"/>
      </div>
      <div className="wi-x44">
        {[['Final Balance', fmtB(finalBalance)], ['Total Profit', (profit >= 0 ? '+' : '') + fmtB(profit)], ['Total Return', `${totalReturn >= 0 ? '+' : ''}${totalReturn.toFixed(1)}%`]].map(([l, v]) => (
          <div key={l} className="wi-x45">
            <div className="txt-micro txt-muted wi-x46">{l}</div>
            <div className="num-sm wi-x47" style={{
              color: totalReturn >= 0 ? 'var(--green)' : 'var(--red)'
            }}>{v}</div>
          </div>
        ))}
      </div>
      {monthlyReturn < 0 && (
        <div className="wi-x48">
          <div className="txt-caption wi-x49">⚠️ Avg monthly return is negative. Use the simulator filters to improve your edge before projecting forward.</div>
        </div>
      )}
    </Panel>
  );
}

/* Teaser shown inside the FeatureGate blur for CompoundProjector.
 * Shows the user's REAL avg monthly return + a partial sparkline
 * covering the months they've already traded. The 12-month future
 * projection is the blurred/locked part — that's the upgrade hook.
 * Props:
 *   trades       — full trade array (same as rest of WhatIfPage)
 *   startBalance — default $10 000, user can't change it while locked
 */
function CompoundProjectorTeaser({ trades = [], activeAccount }) {
  const START_BALANCE = Math.max(100, parseFloat(activeAccount?.balance) || 10_000);
  const FUTURE_MONTHS = 12;

  // ── Calculate real avg monthly return from user's trades ──────────────────
  const { monthlyReturn, tradedMonths, monthlyPnlArr } = useMemo(() => {
    if (!trades.length) return { monthlyReturn: 0, tradedMonths: 0, monthlyPnlArr: [] };
    const byMonth = {};
    trades.forEach(t => {
      const key = (t.date || '').slice(0, 7); // 'YYYY-MM'
      if (key) byMonth[key] = (byMonth[key] || 0) + Number(t.result);
    });
    const vals = Object.values(byMonth);
    if (!vals.length) return { monthlyReturn: 0, tradedMonths: 0, monthlyPnlArr: [] };
    const avgPnl = vals.reduce((a, v) => a + v, 0) / vals.length;
    return {
      monthlyReturn: (avgPnl / START_BALANCE) * 100,
      tradedMonths:  vals.length,
      monthlyPnlArr: vals,
    };
  }, [trades]);

  // ── Historical equity points (what already happened) ─────────────────────
  const historicalPts = useMemo(() => {
    const pts = [START_BALANCE];
    monthlyPnlArr.forEach(pnl => pts.push(pts[pts.length - 1] + pnl));
    return pts;
  }, [monthlyPnlArr]);

  // ── Future projection points (the locked part) ────────────────────────────
  const futurePts = useMemo(() => {
    const r = monthlyReturn / 100;
    const base = historicalPts[historicalPts.length - 1];
    const pts = [base];
    for (let i = 1; i <= FUTURE_MONTHS; i++) pts.push(pts[pts.length - 1] * (1 + r));
    return pts;
  }, [historicalPts, monthlyReturn]);

  const projectedFinal  = futurePts[futurePts.length - 1];
  const projectedProfit = projectedFinal - START_BALANCE;
  const projectedReturn = (projectedProfit / START_BALANCE) * 100;
  const positive        = monthlyReturn >= 0;
  const fmtB = n => n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${Math.round(Math.abs(n)).toLocaleString()}`;

  // ── Sparkline: historical (solid) + future (dashed, semi-opaque) ──────────
  const sparkRef = useRef(null);
  useEffect(() => {
    const cv = sparkRef.current; if (!cv) return;
    const dpr = window.devicePixelRatio || 1;
    const W = cv.parentElement?.offsetWidth || 300, H = 90;
    cv.width = W * dpr; cv.height = H * dpr;
    cv.style.width = W + 'px'; cv.style.height = H + 'px';
    const ctx = cv.getContext('2d'); ctx.scale(dpr, dpr); ctx.clearRect(0, 0, W, H);

    // Combine both segments for y-axis scaling
    const allPts = [...historicalPts, ...futurePts.slice(1)];
    const mn = Math.min(...allPts), mx = Math.max(...allPts);
    const range = Math.max(mx - mn, 1);
    const pad = { l:8, r:8, t:8, b:8 };
    const totalLen = allPts.length;
    const xs = i => pad.l + (i / (totalLen - 1)) * (W - pad.l - pad.r);
    const ys = v => H - pad.b - ((v - mn) / range) * (H - pad.t - pad.b);

    const histLen = historicalPts.length;
    const color   = positive ? '#3DBE73' : '#D94F4F';
    const colorA  = positive ? 'rgba(61,190,115,' : 'rgba(217,79,79,';

    // Historical area + line (solid)
    if (histLen >= 2) {
      ctx.beginPath();
      ctx.moveTo(xs(0), ys(historicalPts[0]));
      historicalPts.forEach((v, i) => { if (i > 0) ctx.lineTo(xs(i), ys(v)); });
      ctx.lineTo(xs(histLen - 1), H - pad.b); ctx.lineTo(xs(0), H - pad.b); ctx.closePath();
      const grad = ctx.createLinearGradient(0, 0, 0, H);
      grad.addColorStop(0, colorA + '.18)'); grad.addColorStop(1, colorA + '0)');
      ctx.fillStyle = grad; ctx.fill();
      ctx.beginPath(); ctx.moveTo(xs(0), ys(historicalPts[0]));
      historicalPts.forEach((v, i) => { if (i > 0) ctx.lineTo(xs(i), ys(v)); });
      ctx.strokeStyle = color; ctx.lineWidth = 2; ctx.lineJoin = 'round'; ctx.stroke();
    }

    // Future segment (dashed, dimmed) — visually signals "this is the locked part"
    if (futurePts.length >= 2) {
      const offset = histLen - 1; // index of the join point
      ctx.setLineDash([4, 4]);
      ctx.globalAlpha = 0.45;
      ctx.beginPath();
      ctx.moveTo(xs(offset), ys(futurePts[0]));
      futurePts.forEach((v, i) => { if (i > 0) ctx.lineTo(xs(offset + i), ys(v)); });
      ctx.strokeStyle = color; ctx.lineWidth = 2; ctx.lineJoin = 'round'; ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;
    }

    // Divider line between past and future
    if (histLen > 1 && histLen < totalLen) {
      const x = xs(histLen - 1);
      ctx.beginPath(); ctx.moveTo(x, pad.t); ctx.lineTo(x, H - pad.b);
      ctx.strokeStyle = getThemeColor('--wi-surface-180', 'rgba(255, 255, 255, 0.18)'); ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]); ctx.stroke(); ctx.setLineDash([]);
    }
  }, [historicalPts, futurePts, positive]);

  const hasData = trades.length > 0;

  return (
    <Panel>
      {/* Header */}
      <div className="wi-x50">
        <div>
          <div className="h5 wi-x51">📈 Compound Growth Projector</div>
          <div className="txt-caption txt-muted">
            {hasData
              ? `Based on ${tradedMonths} month${tradedMonths !== 1 ? 's' : ''} of your trading data`
              : 'Log trades to see your real projection'}
          </div>
        </div>
        {/* Real avg monthly return badge — this is LIVE data, not demo */}
        {hasData && (
          <div
            className="wi-x52"
            style={{
              background: positive ? 'var(--wi-green-80)' : 'var(--wi-red-80)',
              border: `1px solid ${positive ? 'var(--wi-green-200)' : 'var(--wi-red-200)'}`
            }}>
            <span className="txt-micro" style={{ color: positive ? 'var(--green)' : 'var(--red)' }}>Your Avg Monthly</span>
            <span className="num-sm wi-x53" style={{
              color: positive ? 'var(--green)' : 'var(--red)'
            }}>
              {positive ? '+' : ''}{monthlyReturn.toFixed(2)}%
            </span>
          </div>
        )}
      </div>
      {/* Context line — the actual upgrade pitch */}
      {hasData && (
        <div
          className="wi-x54"
          style={{
            background: positive ? 'var(--wi-green-50)' : 'var(--wi-red-50)',
            border: `1px solid ${positive ? 'var(--wi-green-150)' : 'var(--wi-red-150)'}`
          }}>
          <div className="txt-caption wi-x55" style={{
            color: positive ? 'var(--green)' : 'var(--red)'
          }}>
            {positive
              ? `Your avg monthly return is ${monthlyReturn.toFixed(2)}%. Here's what $10K becomes in ${FUTURE_MONTHS} months if you maintain this pace.`
              : `Your avg monthly return is ${monthlyReturn.toFixed(2)}%. Unlock to see your projections and find the rules that flip this positive.`}
          </div>
        </div>
      )}
      {/* Static controls */}
      <div className="wi-x56">
        <div>
          <div className="txt-micro txt-muted wi-x57">Starting Balance</div>
          <div className="wi-pip-badge">
            <span className="wi-x58">$</span>
            <span className="wi-x59">10,000</span>
          </div>
        </div>
        <div>
          <Slider label="Projection Months" value={FUTURE_MONTHS} min={1} max={36} step={1}
            format={v => `${v}mo`} onChange={() => {}} disabled color="var(--blue)"/>
        </div>
      </div>
      {/* Sparkline — historical solid, future dashed */}
      <div className="wi-x60">
        <canvas ref={sparkRef} className="wi-x61"/>
        {/* "Future →" label floats over the dashed section */}
        {hasData && (
          <div className="wi-x62">
            Projected →
          </div>
        )}
      </div>
      <div className="wi-x63">
        <span className="txt-micro wi-x64">
          ← {tradedMonths} months actual
        </span>
        <span className="txt-micro wi-x65">
          {FUTURE_MONTHS} months projected →
        </span>
      </div>
      {/* Projected outcome stats — avg monthly + starting balance visible; final balance blurred (locked) */}
      <div className="wi-x66">
        {/* Visible: Starting balance reference */}
        <div className="wi-x67">
          <div className="txt-micro txt-muted wi-x68">Starting Balance</div>
          <div className="num-sm wi-x69">{fmtB(START_BALANCE)}</div>
        </div>
        {/* Visible: avg monthly return */}
        <div className="wi-x70">
          <div className="txt-micro txt-muted wi-x71">Avg Monthly</div>
          <div className="num-sm wi-x72" style={{
            color: positive ? 'var(--green)' : 'var(--red)'
          }}>
            {hasData ? (positive ? '+' : '') + monthlyReturn.toFixed(2) + '%' : '—'}
          </div>
        </div>
        {/* Blurred: 12-month projected final balance */}
        <div className="wi-x73">
          <div className="wi-x74">
            <div className="txt-micro txt-muted wi-x75">Final Balance</div>
            <div className="num-sm wi-x76" style={{
              color: positive ? 'var(--green)' : 'var(--red)'
            }}>{fmtB(projectedFinal)}</div>
          </div>
          <div
            className="wi-x77"
            style={{
              border:`1px solid rgba(${positive ? '61,190,115' : '217,79,79'},.2)`
            }}>
            <span className="wi-x78">🔒</span>
            <span
              className="wi-x79"
              style={{
                color: positive ? 'var(--wi-green-700)' : 'var(--wi-red-700)'
              }}>HIDDEN</span>
          </div>
        </div>
      </div>
    </Panel>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   WIDGET 2 — BEST SETUP FINDER
───────────────────────────────────────────────────────────────────────────── */
const MIN_TRADES = 3;
function BestSetupFinder({ trades }) {
  const [metric, setMetric] = useState('score');
  const [infoOpen, setInfoOpen] = useState(false);
  const combos = useMemo(() => {
    const map = {};
    trades.forEach(t => {
      const keys = [t.pair || null, t.session || null, t.strat || null,
        (t.pair && t.session) ? `${t.pair} + ${t.session}` : null,
        (t.pair && t.strat)   ? `${t.pair} + ${t.strat}`   : null,
        (t.session && t.strat)? `${t.session} + ${t.strat}` : null,
      ].filter(Boolean);
      keys.forEach(key => {
        if (!map[key]) map[key] = { n:0, wins:0, pnl:0, rrs:[] };
        map[key].n++; map[key].pnl += Number(t.result);
        if (Number(t.result) > 0) map[key].wins++;
        const rr = parseFloat(t.actualRR || t.rr); if (rr > 0) map[key].rrs.push(rr);
      });
    });
    return Object.entries(map)
      .filter(([, d]) => d.n >= MIN_TRADES)
      .map(([label, d]) => {
        const wrDecimal = d.wins / d.n;
        const avgRR = d.rrs.length ? d.rrs.reduce((a, v) => a + v, 0) / d.rrs.length : 0;
        return { label, n:d.n, wr:Math.round(wrDecimal * 100), pnl:d.pnl, avgRR, score: wrDecimal * avgRR };
      })
      .sort((a, b) => metric === 'wr' ? b.wr - a.wr : metric === 'pnl' ? b.pnl - a.pnl : metric === 'rr' ? b.avgRR - a.avgRR : b.score - a.score)
      .slice(0, 8);
  }, [trades, metric]);
  const maxPnl = Math.max(...combos.map(c => Math.abs(c.pnl)), 1);
  const maxScore = Math.max(...combos.map(c => c.score), 0.01);
  return (
    <Panel>
      <div className="wi-x80">
        <div>
          <div className="h5 wi-x81">🎯 Best Setup Finder</div>
          <div className="txt-caption txt-muted">Your top performing pair × session × strategy combinations</div>
        </div>
        <div className="wi-x82">
          <div className="wi-view-toggle">
            {[['wr','Win Rate'],['pnl','P&L'],['rr','Avg R:R'],['score','Score']].map(([v, l]) => (
              <button
                key={v}
                onClick={() => setMetric(v)}
                className="wi-x83"
                style={{
                  background:metric === v ? 'var(--gold)' : 'transparent',
                  color:metric === v ? 'var(--black,#111)' : 'var(--gray)'
                }}>
                {l}
              </button>
            ))}
          </div>
          <InfoIcon onClick={() => setInfoOpen(true)}/>
        </div>
      </div>
      <InfoPanel open={infoOpen} onClose={() => setInfoOpen(false)} title="Best Setup Finder" icon="🎯">
        <InfoSection heading="WHAT IT DOES">Ranks your pair × session × strategy combinations by the metric you choose. Groups with fewer than {MIN_TRADES} trades are excluded.</InfoSection>
        <InfoTip>Switch to Score mode to find your true edge — Win Rate × Avg R:R rewards both accuracy and reward size.</InfoTip>
      </InfoPanel>
      {combos.length === 0 ? (
        <div className="txt-caption txt-muted wi-x84">Need at least {MIN_TRADES} trades per combination to rank setups.</div>
      ) : (
        <div className="wi-x85">
          {combos.map((c, i) => (
            <SetupRankRow key={c.label} c={c} i={i} metric={metric} maxPnl={maxPnl} maxScore={maxScore}/>
          ))}
        </div>
      )}
      {combos.length > 0 && (
        <div className="wi-x86">
          <div className="txt-caption wi-x87">
            💡 <strong>{combos[0].label}</strong> is your strongest setup — {metric === 'score' ? `${combos[0].score.toFixed(2)} composite score` : metric === 'wr' ? `${combos[0].wr}% win rate` : metric === 'pnl' ? fmt$(combos[0].pnl) + ' P&L' : `${combos[0].avgRR.toFixed(2)}R avg`} across {combos[0].n} trades.
          </div>
        </div>
      )}
    </Panel>
  );
}

/* Teaser for Best Setup Finder — 70/30 reveal pattern.
   Rows #1–2 visible at full opacity; rows #3–4 blurred (locked intelligence).
   The IntelligenceGate wrapper fades the bottom and adds the CTA strip. */
function BestSetupFinderTeaser() {
  const visibleSetups = DEMO.setups.slice(0, 2);
  const lockedSetups  = DEMO.setups.slice(2);
  return (
    <Panel>
      {/* Header */}
      <div className="wi-x88">
        <div>
          <div className="h5 wi-x89">🎯 Best Setup Finder</div>
          <div className="txt-caption txt-muted">Your most profitable pair × session × strategy combinations</div>
        </div>
        <div className="wi-x90">Score mode</div>
      </div>
      {/* Rows #1–2: fully visible */}
      <div className="wi-x91">
        {visibleSetups.map((s, i) => (
          <div key={s.rank} className="wi-x92">
            <span className="wi-x93">#{s.rank}</span>
            <div className="wi-x94">
              <div className="wi-x95">
                <span className="txt-caption fw-semibold wi-x96">{s.label}</span>
                <span className="wi-x97">{s.n} trades</span>
              </div>
              <div className="wi-x98">
                <div className="wi-progress-track-5">
                  <div
                    className="wi-x99"
                    style={{
                      width:`${Math.min(100, s.score / 2.5 * 100)}%`,
                      background:s.color
                    }} />
                </div>
                <span
                  className="wi-x100"
                  style={{
                    color:s.color
                  }}>{s.score.toFixed(2)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Top-setup insight callout — gives real value before the gate */}
      <div className="wi-x101">
        <div className="txt-caption wi-x102">
          💡 <strong>{DEMO.setups[0].label}</strong> is your #1 setup — {DEMO.setups[0].wr}% win rate, {DEMO.setups[0].score.toFixed(2)} composite score across {DEMO.setups[0].n} trades.
        </div>
      </div>
      {/* Rows #3–4: blurred / locked */}
      <div className="wi-x103">
        {lockedSetups.map(s => (
          <div key={s.rank} className="wi-x104">
            <span className="wi-x105">#{s.rank}</span>
            <div className="wi-x106">
              <div className="wi-x107">
                <span className="txt-caption fw-semibold wi-x108">{s.label}</span>
                <span className="wi-x109">{s.n} trades</span>
              </div>
              <div className="wi-x110">
                <div className="wi-progress-track-5">
                  <div
                    className="wi-x111"
                    style={{
                      width:`${Math.min(100, s.score / 2.5 * 100)}%`,
                      background:s.color
                    }} />
                </div>
                <span
                  className="wi-x112"
                  style={{
                    color:s.color
                  }}>{s.score.toFixed(2)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   TIMING HEATMAP — shared constants & helpers
───────────────────────────────────────────────────────────────────────────── */
// Minimum trades in an hour before it qualifies for avoidHour / scoring
const MIN_SAMPLE = 3;

/**
 * Score an hour slot: win-rate × average-|trade| / avgTrade across all hours.
 * Higher = better. Defined outside any component so it is never re-created on render.
 */
function hourScore(h, globalAvgTrade) {
  if (!h.n) return 0;
  const wr  = h.wins / h.n;
  const avg = h.pnl / h.n;
  // normalise average trade value against the account-wide average
  return wr * (globalAvgTrade > 0 ? avg / globalAvgTrade : Math.sign(avg));
}

/* ─────────────────────────────────────────────────────────────────────────────
   WIDGET 3 — TIMING HEATMAP  (redesigned)
───────────────────────────────────────────────────────────────────────────── */
function TimingHeatmap({ trades, activeAccount }) {
  const [view, setView]         = useState('pnl');
  const [infoOpen, setInfoOpen] = useState(false);
  const [tooltip, setTooltip]   = useState(null); // { h, x, y }

  const hourData = useMemo(() => {
    const hours = Array.from({ length:24 }, (_, h) => ({ h, pnl:0, wins:0, n:0 }));
    trades.forEach(t => {
      if (!t.time) return;
      const h = parseInt(t.time.split(':')[0], 10);
      if (isNaN(h) || h < 0 || h > 23) return;
      hours[h].n++; hours[h].pnl += Number(t.result);
      if (Number(t.result) > 0) hours[h].wins++;
    });
    return hours;
  // activeAccount.id included so memo re-runs when account switches
  }, [trades, activeAccount?.id]);

  const tradesWithTime = hourData.reduce((a, v) => a + v.n, 0);
  const totalPnl       = hourData.reduce((a, v) => a + v.pnl, 0);
  const totalWins      = hourData.reduce((a, v) => a + v.wins, 0);
  const winRate        = tradesWithTime > 0 ? Math.round(totalWins / tradesWithTime * 100) : 0;
  const avgTrade       = tradesWithTime > 0 ? totalPnl / tradesWithTime : 0;

  const maxVal = Math.max(...hourData.map(h =>
    view === 'pnl' ? Math.abs(h.pnl) : view === 'wr' ? (h.n ? h.wins / h.n * 100 : 0) : h.n
  ), 1);

  const cellColor = h => {
    if (!h.n) return 'var(--wi-surface-35)';
    if (view === 'pnl') {
      const ratio = Math.abs(h.pnl) / maxVal;
      const a = Math.min(.88, .18 + ratio * .70);
      return h.pnl >= 0 ? `rgba(61,190,115,${a})` : `rgba(217,79,79,${a})`;
    }
    if (view === 'wr') {
      const r = h.wins / h.n;
      const a = Math.min(.88, .15 + r * .73);
      return `rgba(${r >= .5 ? '61,190,115' : '217,79,79'},${a})`;
    }
    return `rgba(201,168,67,${Math.min(.88, .15 + (h.n / maxVal) * .73)})`;
  };

  const cellLabel = h => {
    if (!h.n) return '';
    if (view === 'pnl') return (h.pnl >= 0 ? '+' : '') + (Math.abs(h.pnl) >= 1000 ? `${(h.pnl/1000).toFixed(1)}k` : h.pnl.toFixed(0));
    if (view === 'wr')  return `${Math.round(h.wins / h.n * 100)}%`;
    return h.n;
  };

  const fmt12  = h => h === 0 ? '12am' : h < 12 ? `${h}am` : h === 12 ? '12pm' : `${h-12}pm`;
  const fmtPnl = v => fmt$(v);

  const bestHour  = [...hourData].filter(h => h.n >= 2 && h.pnl > 0).sort((a,b) => b.pnl - a.pnl)[0] ?? null;
  // n>=1 for worst — don't hide a losing hour just because it only happened once
  const worstHour = [...hourData].filter(h => h.n >= 1 && h.pnl < 0).sort((a,b) => a.pnl - b.pnl)[0] ?? null;
  // When no hour is net-negative, fall back to "weakest" — least profitable positive hour (excluding best)
  const weakestHour = worstHour ? null : ([...hourData].filter(h => h.n >= 1 && h.h !== bestHour?.h).sort((a,b) => a.pnl - b.pnl)[0] ?? null);

  // ── avoidHour: derived from hourData (issue 1); excludes worstHour and bestHour to avoid UI duplicates (issues 2, 7)
  // Candidate hours need MIN_SAMPLE trades and must have a negative avgTrade or negative pnl
  const globalAvgTrade = tradesWithTime > 0 ? totalPnl / tradesWithTime : 0;
  const avoidHour = useMemo(() => {
    const excludeH = new Set([bestHour?.h, worstHour?.h].filter(h => h != null));
    return [...hourData]
      .filter(h => h.n >= MIN_SAMPLE && h.pnl < 0 && !excludeH.has(h.h))
      .sort((a, b) => (a.pnl / a.n) - (b.pnl / b.n)) // worst avg trade first
    [0] ?? null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hourData]); // hourData already depends on [trades, activeAccount?.id]

  // ── bestDay / biggestDay: best and worst net P&L days
  const { bestDay, biggestDay } = useMemo(() => {
    const byDate = {};
    trades.forEach(t => {
      const d = t.date || 'unknown';
      if (!byDate[d]) byDate[d] = { date:d, pnl:0, n:0 };
      byDate[d].pnl += Number(t.result);
      byDate[d].n++;
    });
    const days = Object.values(byDate);
    const positive = days.filter(d => d.pnl > 0);
    const negative = days.filter(d => d.pnl < 0);
    return {
      bestDay:    positive.length ? positive.sort((a, b) => b.pnl - a.pnl)[0] : null,
      biggestDay: negative.length ? negative.sort((a, b) => a.pnl - b.pnl)[0] : null,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trades]);

  // Tooltip rendered via a fixed portal-style div so it doesn't get clipped by the panel overflow
  const handleCellEnter = (e, h) => {
    if (!h.n) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({ h, x: rect.left + rect.width / 2, y: rect.top });
  };

  return (
    <Panel className="wi-x113">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="wi-x114">
        <div>
          <div className="h5 wi-x115">⏰ Timing Heatmap</div>
          <div className="txt-caption txt-muted">
            {tradesWithTime === 0 ? 'Add entry times to your trades to unlock this view' : `${tradesWithTime} timed trades${activeAccount?.name ? ` · ${activeAccount.name}` : ''}`}
          </div>
        </div>
        <div className="wi-x116">
          <div className="wi-view-toggle">
            {[['pnl','P&L'],['wr','Win%'],['count','Vol']].map(([v, l]) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className="wi-x117"
                style={{
                  background:view === v ? 'var(--gold)' : 'transparent',
                  color:view === v ? 'var(--black,#111)' : 'var(--gray)'
                }}>
                {l}
              </button>
            ))}
          </div>
          <InfoIcon onClick={() => setInfoOpen(true)}/>
        </div>
      </div>
      <InfoPanel open={infoOpen} onClose={() => setInfoOpen(false)} title="Trade Timing Heatmap" icon="⏰">
        <InfoSection heading="WHAT IT DOES">
          Breaks down your P&L, win rate, or trade volume by the hour you entered each trade.
          Switch between <strong>P&L</strong> (total profit/loss per hour), <strong>Win%</strong> (win rate per hour),
          and <strong>Vol</strong> (number of trades per hour) using the tab buttons in the header.
        </InfoSection>
        <InfoSection heading="THE FOUR INSIGHT CARDS">
          <strong>Best Hour</strong> — the hour with the highest total P&L (≥2 trades required to qualify). Focus your energy here.<br/><br/>
          <strong>Worst Hour</strong> — the single most loss-generating hour. Consider a hard rule to skip this window.<br/><br/>
          <strong>Avoid This Hour</strong> — a distinct hour from Worst that has at least {MIN_SAMPLE} trades and a negative average trade result. This catches a second danger zone that may not have the single largest loss but consistently drains your account.<br/><br/>
          <strong>Best Day</strong> — the calendar date with the highest total P&L. Your benchmark for what a peak session looks like.<br/><br/>
          <strong>Worst Day</strong> — the calendar date where your total P&L was lowest. Knowing your worst day tells you how much you can lose in a single session and whether you need a harder daily stop-loss.<br/><br/>
        </InfoSection>
        <InfoSection heading="CELL OUTLINES">
          Green outline = Best Hour · Red outline = Worst Hour · Amber outline = Avoid Hour. These persist regardless of which view tab is active.
        </InfoSection>
        <InfoSection heading="SCORING METHODOLOGY">
          Hours are scored as <em>win-rate × (avg trade / account avg trade)</em>. This rewards hours that are both accurate and generate above-average dollar results, not just raw P&L (which could be skewed by a single outlier trade).
        </InfoSection>
        <InfoTip>Start by toggling to P&L view and looking for a cluster of green cells. That's your golden window — consider restricting new entries to those hours first.</InfoTip>
      </InfoPanel>
      {tradesWithTime === 0 ? (
        <div className="txt-caption txt-muted wi-x118">
          Log entry times on your trades to see when you perform best and worst.
        </div>
      ) : (
        <>
          {/* ── Summary stat strip ───────────────────────────────────────── */}
          <div className="wi-x119">
            {[
              ['Trades',    String(tradesWithTime),              'var(--wi-text-900)'],
              ['Net P&L',   fmtPnl(totalPnl),                   totalPnl >= 0 ? 'var(--green)' : 'var(--red)'],
              ['Win Rate',  `${winRate}%`,                      winRate >= 50 ? 'var(--gold)' : 'var(--red)'],
              ['Avg Trade', fmt$(avgTrade), avgTrade >= 0 ? 'var(--green)' : 'var(--red)'],
            ].map(([label, val, color]) => (
              <div
                key={label}
                className="wi-x120"
                style={{
                  borderLeft:`2px solid ${color}`
                }}>
                <div className="txt-micro wi-x121">{label}</div>
                <div className="num-sm wi-x122" style={{
                  color
                }}>{val}</div>
              </div>
            ))}
          </div>

          {/* ── Excluded trades warning ───────────────────────────────────── */}
          {trades.length > tradesWithTime && (
            <div className="wi-x123">
              <span className="wi-x124">⚠️</span>
              <span className="txt-caption wi-x125">
                {trades.length - tradesWithTime} trade{trades.length - tradesWithTime !== 1 ? 's' : ''} excluded — no entry time logged.
                Add entry times to include them in this view.
              </span>
            </div>
          )}

          {/* ── Heatmap cells — only hours that have at least 1 trade ─────── */}
          {(() => {
            const activeHours = hourData.filter(h => h.n > 0);
            return (
              <div
                className="wi-x126"
                style={{
                  gridTemplateColumns:`repeat(${activeHours.length}, 1fr)`
                }}
                onMouseLeave={() => setTooltip(null)}>
                {activeHours.map(h => (
                  <div key={h.h} className="wi-x127">
                    <div
                      onMouseEnter={e => handleCellEnter(e, h)}
                      className="wi-x128"
                      style={{
                        background: cellColor(h),

                        outline: bestHour  && h.h === bestHour.h  ? '1.5px solid var(--wi-green-650)'  :
                                 worstHour && h.h === worstHour.h ? '1.5px solid var(--wi-red-650)'   :
                                 avoidHour && h.h === avoidHour.h ? '1.5px solid var(--wi-gold-700)'  : 'none'
                      }}
                      onMouseOver={e => { e.currentTarget.style.transform = 'scaleY(1.12)'; e.currentTarget.style.filter = 'brightness(1.18)'; }}
                      onMouseOut={e  => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.filter = 'none'; }}>
                      {cellLabel(h)}
                    </div>
                    {/* hour label under each active cell */}
                    <span className="wi-x129">
                      {fmt12(h.h)}
                    </span>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* spacer between heatmap and legend */}
          <div className="wi-x130"/>

          {/* ── Legend ────────────────────────────────────────────────────── */}
          <div className="wi-x131">
            <span className="txt-micro wi-x132">Loss</span>
            <div className="wi-x133"/>
            <span className="txt-micro wi-x134">Profit</span>
          </div>

          {/* ── Best / Worst / Avoid / Worst-Day cards ────────────────────── */}
          {(bestHour || worstHour || weakestHour || avoidHour || bestDay || biggestDay) && (
            <div className="wi-x135">
              {bestHour && (
                <div className="wi-x136">
                  <div className="txt-micro wi-x137">BEST HOUR</div>
                  <div className="num-sm wi-x138">{fmt12(bestHour.h)}</div>
                  <div className="txt-caption wi-x139">{fmt$(bestHour.pnl)} P&L</div>
                  <div className="txt-micro wi-x140">
                    {bestHour.n} trades · {Math.round(bestHour.wins/bestHour.n*100)}% WR
                  </div>
                </div>
              )}
              {(worstHour || weakestHour) && (() => {
                const h = worstHour || weakestHour;
                const isWorst = !!worstHour;
                return (
                  <div
                    className="wi-x141"
                    style={{
                      borderLeft:`2px solid ${isWorst ? 'var(--red)' : 'var(--wi-gold-400)'}`
                    }}>
                    <div className="txt-micro wi-x142" style={{
                      color: isWorst ? 'var(--wi-red-500)' : 'var(--wi-gold-400)'
                    }}>
                      {isWorst ? 'WORST HOUR' : 'WEAKEST HOUR'}
                    </div>
                    <div className="num-sm wi-x143" style={{
                      color: isWorst ? 'var(--red)' : 'var(--wi-gold-700)'
                    }}>{fmt12(h.h)}</div>
                    <div className="txt-caption wi-x144" style={{
                      color: isWorst ? 'var(--wi-red-800)' : 'var(--wi-gold-600)'
                    }}>{fmt$(h.pnl)} P&L</div>
                    <div className="txt-micro wi-x145">
                      {h.n} trades · {Math.round(h.wins/h.n*100)}% WR
                    </div>
                  </div>
                );
              })()}
              {/* avoidHour: only shown when distinct from worstHour (guaranteed by derivation above) */}
              {avoidHour && (
                <div className="wi-x146">
                  <div className="txt-micro wi-x147">AVOID THIS HOUR</div>
                  <div className="num-sm wi-x148">{fmt12(avoidHour.h)}</div>
                  {/* avgLoss is negative; use fmtPnl so the sign is always rendered correctly (issue 10) */}
                  <div className="txt-caption wi-x149">
                    {fmtPnl(avoidHour.pnl / avoidHour.n)} avg trade
                  </div>
                  <div className="txt-micro wi-x150">
                    {avoidHour.n} trades · {Math.round(avoidHour.wins/avoidHour.n*100)}% WR
                  </div>
                </div>
              )}
              {bestDay && (
                <div className="wi-x151">
                  <div className="txt-micro wi-x152">BEST DAY</div>
                  <div className="num-sm wi-x153">{fmtPnl(bestDay.pnl)}</div>
                  <div className="txt-caption wi-x154">
                    {bestDay.date !== 'unknown'
                      ? new Date(bestDay.date + 'T00:00:00').toLocaleDateString(undefined, { month:'short', day:'numeric', year:'numeric' })
                      : '—'}
                  </div>
                  <div className="txt-micro wi-x155">{bestDay.n} trade{bestDay.n !== 1 ? 's' : ''}</div>
                </div>
              )}
              {biggestDay && (
                <div className="wi-x156">
                  <div className="txt-micro wi-x157">WORST DAY</div>
                  <div className="num-sm wi-x158">{fmtPnl(biggestDay.pnl)}</div>
                  <div className="txt-caption wi-x159">
                    {biggestDay.date !== 'unknown'
                      ? new Date(biggestDay.date + 'T00:00:00').toLocaleDateString(undefined, { month:'short', day:'numeric', year:'numeric' })
                      : '—'}
                  </div>
                  <div className="txt-micro wi-x160">{biggestDay.n} trade{biggestDay.n !== 1 ? 's' : ''}</div>
                </div>
              )}
            </div>
          )}

          {/* ── Floating tooltip ──────────────────────────────────────────── */}
          {tooltip && (() => {
            const h = tooltip.h;
            const wr = h.n ? Math.round(h.wins / h.n * 100) : 0;
            const avg = h.n ? h.pnl / h.n : 0;
            return (
              <div
                className="wi-x161"
                style={{
                  left: tooltip.x,
                  top: tooltip.y - 8
                }}>
                <div className="wi-x162">
                  {fmt12(h.h)}
                </div>
                {[
                  ['Trades',    String(h.n),                         'var(--wi-text-700)'],
                  ['Win Rate',  `${wr}%`,                            wr >= 50 ? 'var(--green)' : 'var(--red)'],
                  ['P&L',       fmtPnl(h.pnl),                      h.pnl >= 0 ? 'var(--green)' : 'var(--red)'],
                  ['Avg Trade', fmt$(avg),   avg >= 0 ? 'var(--green)' : 'var(--red)'],
                ].map(([label, val, color]) => (
                  <div key={label} className="wi-x163">
                    <span className="wi-x164">{label}</span>
                    <span
                      className="wi-x165"
                      style={{
                        color
                      }}>{val}</span>
                  </div>
                ))}
              </div>
            );
          })()}
        </>
      )}
    </Panel>
  );
}

/* Teaser for Timing Heatmap — 70/30 reveal.
   Best Window card fully visible; Worst Window + Avoid Hour blurred (locked).
   A "hidden intelligence" strip shows the count of hidden insights. */
function TimingHeatmapTeaser({ trades = [] }) {
  const fmt12 = h => h === 0 ? '12am' : h < 12 ? `${h}am` : h === 12 ? '12pm' : `${h - 12}pm`;

  const { data, bestHour, worstHour } = useMemo(() => {
    const hours = Array.from({ length:24 }, (_, h) => ({ h, pnl:0, n:0 }));
    trades.forEach(t => {
      if (!t.time) return;
      const h = parseInt(t.time.split(':')[0], 10);
      if (isNaN(h) || h < 0 || h > 23) return;
      hours[h].n++;
      hours[h].pnl += Number(t.result);
    });
    const data = hours.map(h => h.n ? h.pnl : 0);
    const qualifiedBest  = hours.filter(h => h.n >= MIN_SAMPLE);
    const qualifiedWorst = hours.filter(h => h.n >= 1);
    const bestHour  = qualifiedBest.length  ? [...qualifiedBest].filter(h => h.pnl > 0).sort((a, b) => b.pnl - a.pnl)[0] ?? null : null;
    const worstHour = qualifiedWorst.length ? [...qualifiedWorst].filter(h => h.pnl < 0).sort((a, b) => a.pnl - b.pnl)[0] ?? null : null;
    return { data, bestHour, worstHour };
  }, [trades]);

  const maxV = Math.max(...data.map(Math.abs), 0.01);
  const barH = v => 28 + Math.round((Math.abs(v) / maxV) * 24);
  const cellBg = v => {
    if (v === 0) return 'var(--wi-surface-40)';
    if (v > 0) return `rgba(61,190,115,${Math.min(.9, .18 + (v / maxV) * .72)})`;
    return `rgba(217,79,79,${Math.min(.9, .18 + (Math.abs(v) / maxV) * .72)})`;
  };

  const bestIdx  = data.indexOf(Math.max(...data));
  const worstIdx = data.indexOf(Math.min(...data));

  // Count hours with data for "X more windows analysed" subline
  const hoursWithData = data.filter(v => v !== 0).length;

  return (
    <Panel>
      {/* Header */}
      <div className="wi-x166">
        <div>
          <div className="h5 wi-x167">⏰ Timing Heatmap</div>
          <div className="txt-caption txt-muted">P&amp;L by entry hour — find your golden window</div>
        </div>
        <div className="wi-view-toggle">
          {[['pnl','P&L'],['wr','Win%'],['count','Vol']].map(([v, l]) => (
            <div
              key={v}
              className="wi-x168"
              style={{
                background: v === 'pnl' ? 'var(--gold)' : 'transparent',
                color: v === 'pnl' ? 'var(--black,#111)' : 'var(--gray)'
              }}>{l}</div>
          ))}
        </div>
      </div>
      {/* Bar chart — fully visible */}
      <div className="wi-x169">
        {data.map((v, i) => (
          <div
            key={i}
            className="wi-x170"
            style={{
              height:barH(v),
              background:cellBg(v),

              outline: i === bestIdx  ? '1.5px solid var(--wi-green-600)'  :
                       i === worstIdx ? '1.5px solid var(--wi-red-600)'   : 'none'
            }} />
        ))}
      </div>
      <div className="wi-x171">
        {[0,4,8,12,16,20,23].map(h => <span key={h}>{fmt12(h)}</span>)}
      </div>
      <div className="wi-x172">
        {[['var(--wi-green-550)','Profitable'],['var(--wi-red-550)','Losing']].map(([c,l]) => (
          <div key={l} className="wi-x173">
            <div
              className="wi-x174"
              style={{
                background:c
              }} />
            <span className="txt-micro wi-x175">{l}</span>
          </div>
        ))}
      </div>
      {/* Best window — fully visible */}
      <div className="wi-x176">
        <div className="wi-x177">
          <div className="txt-micro wi-x178">BEST WINDOW</div>
          {bestHour ? (
            <>
              <div className="num-sm wi-x179">{fmt12(bestHour.h)}</div>
              <div className="txt-caption wi-x180">
                {fmt$(bestHour.pnl / bestHour.n)} avg
              </div>
            </>
          ) : (
            <div className="txt-caption txt-muted">—</div>
          )}
        </div>

        {/* Worst window — blurred (locked) */}
        <div className="wi-x181">
          <div className="wi-x182">
            <div className="txt-micro wi-x183">WORST WINDOW</div>
            {worstHour ? (
              <>
                <div className="num-sm wi-x184">{fmt12(worstHour.h)}</div>
                <div className="txt-caption wi-x185">
                  {fmt$(worstHour.pnl / worstHour.n)} avg
                </div>
              </>
            ) : (
              <div className="txt-caption txt-muted">██████</div>
            )}
          </div>
          {/* Micro lock overlay on worst card */}
          <div className="wi-x186">
            <span className="wi-x187">🔒</span>
            <span className="wi-x188">HIDDEN</span>
          </div>
        </div>
      </div>
      {/* Hidden insight count strip */}
      {hoursWithData > 0 && (
        <div className="wi-x189">
          <span className="wi-x190">◈</span>
          <span className="txt-caption wi-x191">
            <strong className="wi-x192">{Math.max(2, hoursWithData - 1)} more windows</strong> analysed — Avoid Hour, Day-of-Week, and Best Day data hidden
          </span>
        </div>
      )}
    </Panel>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   WIDGET 4 — RISK SIZING
───────────────────────────────────────────────────────────────────────────── */
function RiskSizingWidget({ trades, activeAccount }) {
  const initBalance = Math.max(100, parseFloat(activeAccount?.balance) || 10_000);
  const [accountSize, setAccountSize]       = useState(initBalance);
  const [accountSizeStr, setAccountSizeStr] = useState(String(initBalance));
  const [riskPct, setRiskPct]               = useState(1);
  const [infoOpen, setInfoOpen]             = useState(false);

  // Sync when account switches
  useEffect(() => {
    const b = Math.max(100, parseFloat(activeAccount?.balance) || 10_000);
    setAccountSize(b);
    setAccountSizeStr(String(b));
  }, [activeAccount?.id, activeAccount?.balance]);

  const { riskPerTrade, rrAtRisk, projectedFinal, riskDiff } = useMemo(() => {
    const riskPerTrade = accountSize * (riskPct / 100);
    const rrTrades = trades.filter(t => parseFloat(t.actualRR || t.rr) > 0);
    const rrAtRisk = rrTrades.length ? rrTrades.reduce((a, t) => a + parseFloat(t.actualRR || t.rr), 0) / rrTrades.length : 0;
    const actual = trades.reduce((a, t) => a + Number(t.result), 0);
    const totalActualRisk = trades.reduce((a, t) => {
      const rr = parseFloat(t.actualRR || t.rr); if (!rr || rr <= 0) return a;
      return a + Math.abs(Number(t.result) / rr);
    }, 0);
    const avgActualRisk = trades.length ? totalActualRisk / trades.length : 0;
    const scale = avgActualRisk > 0 ? riskPerTrade / avgActualRisk : 1;
    const projectedFinal = actual * scale;
    const riskDiff = projectedFinal - actual;
    return { riskPerTrade, rrAtRisk, projectedFinal, riskDiff };
  }, [trades, accountSize, riskPct]);

  return (
    <Panel>
      <div className="wi-x193">
        <div>
          <div className="h5 wi-x194">⚖️ Position Sizing Impact</div>
          <div className="txt-caption txt-muted">See how changing your risk % changes your P&amp;L</div>
        </div>
        <InfoIcon onClick={() => setInfoOpen(true)}/>
      </div>
      <InfoPanel open={infoOpen} onClose={() => setInfoOpen(false)} title="Position Sizing Impact" icon="⚖️">
        <InfoSection heading="WHAT IT DOES">Recalculates your trade history as if you had risked a different percentage per trade.</InfoSection>
        <InfoTip>Most professionals risk 0.5–1% per trade. Going above 2% dramatically increases drawdown risk.</InfoTip>
      </InfoPanel>
      <div className="wi-x195">
        <div>
          <div className="txt-micro txt-muted wi-x196">Account Size</div>
          <div className="wi-pip-badge">
            <span className="wi-x197">$</span>
            <input type="number" value={accountSizeStr} min={100} max={10_000_000} step={1000}
              onChange={e => { setAccountSizeStr(e.target.value); const n = Number(e.target.value); if (n >= 100) setAccountSize(n); }}
              onBlur={e => { const n = Math.max(100, Number(e.target.value) || 100); setAccountSize(n); setAccountSizeStr(String(n)); }}
              className="wi-x198"/>
          </div>
        </div>
        <div><Slider label="Risk Per Trade" value={riskPct} min={0.25} max={5} step={0.25} format={v => `${v}%`} onChange={setRiskPct} color="var(--blue)"/></div>
      </div>
      <div className="wi-x199">
        {[
          ['$ Risk/Trade', `$${riskPerTrade.toLocaleString('en-US', { maximumFractionDigits:0 })}`, 'var(--gold)'],
          ['Avg R:R',      rrAtRisk > 0 ? `${rrAtRisk.toFixed(2)}R` : '—', 'var(--blue)'],
          ['Projected P&L', fmt$(projectedFinal), projectedFinal >= 0 ? 'var(--green)' : 'var(--red)'],
          ['vs Actual', fmt$(riskDiff), riskDiff >= 0 ? 'var(--green)' : 'var(--red)'],
        ].map(([l, v, c]) => (
          <div key={l} className="wi-x200">
            <div className="txt-micro txt-muted wi-x201">{l}</div>
            <div className="num-sm wi-x202" style={{
              color:c
            }}>{v}</div>
          </div>
        ))}
      </div>
      {riskPct > 2 ? (
        <div className="wi-x203">
          <div className="txt-caption wi-x204">⚠️ Above 2% risk significantly increases drawdown. Most professionals stay at 0.5–1%.</div>
        </div>
      ) : riskPct <= 1 && riskPct >= 0.5 ? (
        <div className="wi-x205">
          <div className="txt-caption wi-x206">✅ {riskPct}% risk per trade is within professional standards.</div>
        </div>
      ) : null}
    </Panel>
  );
}

/* Teaser for Risk Sizing — 70/30 reveal.
   0.5% and 1% scenarios fully visible; 2% bar blurred.
   A "hidden optimizer" note teases the interactive drag slider. */
function RiskSizingTeaser({ trades = [], activeAccount }) {
  const ACCOUNT_SIZE = Math.max(100, parseFloat(activeAccount?.balance) || 10_000);
  const { realAvgRR, realActualPnL, avgActualRisk } = useMemo(() => {
    const rrTrades = trades.filter(t => parseFloat(t.actualRR || t.rr) > 0);
    const realAvgRR = rrTrades.length
      ? rrTrades.reduce((a, t) => a + parseFloat(t.actualRR || t.rr), 0) / rrTrades.length
      : 2.3;
    const realActualPnL = trades.reduce((a, t) => a + Number(t.result), 0);
    const totalActualRisk = trades.reduce((a, t) => {
      const rr = parseFloat(t.actualRR || t.rr); if (!rr || rr <= 0) return a;
      return a + Math.abs(Number(t.result) / rr);
    }, 0);
    const avgActualRisk = trades.length ? totalActualRisk / trades.length : 0;
    return { realAvgRR, realActualPnL, avgActualRisk };
  }, [trades]);

  const scalePnl = (riskPct) => {
    const targetRisk = ACCOUNT_SIZE * (riskPct / 100);
    const scale = avgActualRisk > 0 ? targetRisk / avgActualRisk : 1;
    return Math.round(realActualPnL * scale);
  };

  const pnl05 = scalePnl(0.5);
  const pnl1  = scalePnl(1.0);
  const pnl2  = scalePnl(2.0);
  const vsActual = pnl1 - realActualPnL;

  const visibleScenarios = [
    { label:'0.5% risk', rpt: Math.round(ACCOUNT_SIZE * 0.005), pnl: pnl05, rr: realAvgRR, tag: null },
    { label:'1% risk',   rpt: Math.round(ACCOUNT_SIZE * 0.01),  pnl: pnl1,  rr: realAvgRR, tag: 'optimal' },
  ];
  const lockedScenario = { label:'2% risk', rpt: Math.round(ACCOUNT_SIZE * 0.02), pnl: pnl2, rr: realAvgRR, tag: 'high-risk' };

  const maxPnl = Math.max(...[pnl05, pnl1, pnl2].map(Math.abs), 1);

  return (
    <Panel>
      {/* Header */}
      <div className="wi-x207">
        <div>
          <div className="h5 wi-x208">⚖️ Position Sizing Impact</div>
          <div className="txt-caption txt-muted">How your risk % changes projected P&amp;L</div>
        </div>
        <div className="wi-x209">
          INTERACTIVE ON PRO
        </div>
      </div>
      {/* Visible scenarios */}
      <div className="wi-x210">
        {visibleScenarios.map(s => {
          const barPct = Math.round((Math.abs(s.pnl) / maxPnl) * 100);
          const isOptimal = s.tag === 'optimal';
          const isNeg = s.pnl < 0;
          const barColor = isNeg ? 'var(--wi-red-650)' : isOptimal ? '#3DBE73' : 'var(--wi-green-400)';
          return (
            <div key={s.label}>
              <div className="wi-x211">
                <div className="wi-x212">
                  <span className="txt-caption fw-semibold" style={{ color: isOptimal ? 'var(--wi-text-900)' : 'var(--wi-text-550)' }}>
                    {s.label}
                  </span>
                  {isOptimal && (
                    <span className="wi-x213">OPTIMAL</span>
                  )}
                </div>
                <span
                  className="wi-x214"
                  style={{
                    color: isNeg ? 'var(--red)' : 'var(--green)'
                  }}>
                  {fmt$(s.pnl)}
                </span>
              </div>
              <div className="wi-progress-track-6">
                <div
                  className="wi-x215"
                  style={{
                    width:`${barPct}%`,
                    background:barColor,
                    boxShadow: isOptimal ? '0 0 8px var(--wi-green-300)' : 'none'
                  }} />
              </div>
              <div className="wi-x216">
                <span className="wi-x217">${s.rpt}/trade · {s.rr.toFixed(1)}R avg</span>
              </div>
            </div>
          );
        })}
      </div>
      {/* Blurred 2% scenario */}
      <div className="wi-x218">
        <div className="wi-x219">
          <div className="wi-x220">
            <div className="wi-x221">
              <span className="txt-caption fw-semibold wi-x222">{lockedScenario.label}</span>
              <span className="wi-x223">ELEVATED DD</span>
            </div>
            <span className="wi-x224">{fmt$(lockedScenario.pnl)}</span>
          </div>
          <div className="wi-progress-track-6">
            <div
              className="wi-x225"
              style={{
                width:`${Math.round((Math.abs(lockedScenario.pnl) / maxPnl) * 100)}%`
              }} />
          </div>
        </div>
      </div>
      {/* Insight callout — always visible */}
      <div
        className="wi-x226"
        style={{
          background: vsActual >= 0 ? 'var(--wi-green-70)' : 'var(--wi-red-70)',
          border:`1px solid ${vsActual >= 0 ? 'var(--wi-green-200)' : 'var(--wi-red-200)'}`
        }}>
        <div className="txt-caption wi-x227" style={{
          color: vsActual >= 0 ? 'var(--green)' : 'var(--red)'
        }}>
          {trades.length === 0
            ? '💡 Log trades to see how your risk % affects your real P&L.'
            : vsActual >= 0
              ? <>💡 Standardising to 1% risk per trade would add <strong>{fmt$(vsActual)}</strong> vs your actual results.</>
              : <>⚠️ Your current risk sizing is above 1% on average. Reducing to 1% would improve consistency.</>
          }
        </div>
      </div>
    </Panel>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   UPGRADE BANNER  (right-side premium CTA)
───────────────────────────────────────────────────────────────────────────── */
function UpgradeBanner({ onUpgrade }) {
  const [hov, setHov] = useState(false);
  return (
    <div className="wi-x228">
      {/* decorative glow */}
      <div className="wi-x229"/>
      <div className="wi-x230">PREMIUM</div>
      <div className="txt-body-sm fw-semibold wi-x231">Unlock the full simulator</div>
      <ul className="wi-x232">
        {['Compound growth projections','Best setup finder & scoring','Hourly timing heatmap',
          'Risk sizing optimizer','AI trade pattern insights','Trader DNA profiling','Goal tracker & milestones',
        ].map(f => (
          <li key={f} className="wi-x233">
            <span className="wi-x234">✦</span>{f}
          </li>
        ))}
      </ul>
      <button
        onClick={onUpgrade}
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        className="wi-x235"
        style={{
          background: hov ? '#EDD67A' : 'var(--gold)',
          boxShadow: hov ? '0 0 20px var(--wi-gold-350)' : 'none'
        }}>
        UPGRADE TO PREMIUM
      </button>
      <div className="txt-micro txt-muted wi-x236">From $19/mo · Cancel anytime</div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   AI INSIGHTS TEASER  (right panel)
───────────────────────────────────────────────────────────────────────────── */
const INSIGHT_COLORS = {
  green: { label:'var(--green)', border:'var(--wi-green-200)',  bg:'var(--wi-green-60)',  accent:'#3DBE73' },
  gold:  { label:'var(--gold)',  border:'var(--wi-gold-200)',  bg:'var(--wi-gold-60)',  accent:'#C9A84C' },
  blue:  { label:'#4A9EFF',      border:'var(--wi-blue-200)', bg:'var(--wi-blue-60)', accent:'#4A9EFF' },
  red:   { label:'var(--red)',   border:'var(--wi-red-200)',   bg:'var(--wi-red-60)',   accent:'#D94F4F' },
};

function InsightCard({ type = 'gold', icon, title, val, text, locked, onUpgrade }) {
  const c = INSIGHT_COLORS[type] || INSIGHT_COLORS.gold;
  return (
    <div
      className="wi-x237"
      style={{
        border:`1px solid ${c.border}`
      }}>
      {/* top accent line */}
      <div
        className="wi-x238"
        style={{
          background:`linear-gradient(90deg,${c.accent},transparent)`
        }} />
      <div style={{ filter: locked ? 'blur(4px)' : 'none', userSelect: locked ? 'none' : 'auto',
        opacity: locked ? .55 : 1, pointerEvents: locked ? 'none' : 'auto' }}>
        <div
          className="wi-x239"
          style={{
            color:c.label
          }}>{icon} {title}</div>
        <div
          className="wi-x240"
          style={{
            color:c.label
          }}>{val}</div>
        <div className="txt-caption wi-x241">{text}</div>
      </div>
      {locked && (
        <div className="wi-x242">
          <div className="wi-x243">🔒</div>
          <div
            className="wi-x244"
            style={{
              color:c.label
            }}>PREMIUM INSIGHT</div>
          <button
            onClick={onUpgrade}
            className="wi-x245"
            style={{
              background:c.accent
            }}>UNLOCK</button>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   GOAL TRACKER TEASER
───────────────────────────────────────────────────────────────────────────── */
function GoalTrackerTeaser() {
  return (
    <Panel>
      <div className="wi-x246">
        <div className="txt-micro wi-x247">MILESTONES & UNLOCKS</div>
        <span className="wi-x248">
          EXAMPLE
        </span>
      </div>
      <div className="wi-milestones">
        {DEMO.milestones.map(m => (
          <div key={m.label} className="wi-x249">
            <div className="txt-caption fw-semibold wi-x250">{m.label}</div>
            <div className="txt-micro txt-muted wi-x251">{m.pct}% complete</div>
            <div className="wi-progress-track-3">
              <div
                className="wi-x252"
                style={{
                  width:`${m.pct}%`,
                  background:m.color
                }} />
            </div>
            <div className="txt-micro txt-muted">{m.current} / {m.target}</div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

/* TEASER — KPI CARDS */
function KpiCardsTeaser() {
  return (
    <div className="wi-kpis">
      {[
        ['Actual P&L',    '-$1,240', 'var(--red)'],
        ['Simulated P&L', '+$3,860', 'var(--green)'],
        ['Difference',    '+$5,100', 'var(--green)'],
        ['Trades Skipped','31 / 147','var(--gold)'],
      ].map(([l, v, c]) => (
        <Panel key={l} className="wi-x253">
          <div className="txt-micro wi-x254">{l}</div>
          <div className="num-md wi-x255" style={{
            color:c
          }}>{v}</div>
          {l === 'Difference' && (
            <div className="txt-micro wi-x256">+411.3% vs actual</div>
          )}
        </Panel>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   REDESIGNED PAGE HEADER
───────────────────────────────────────────────────────────────────────────── */
function WhatIfPageHeader({ trades, user, activeAccount, onUpgrade, anyRuleActive, activeRuleCount, dna, setDnaOpen, setCustomizeOpen, hiddenCount }) {
  const userPlan = user?.plan ?? 'free';

  return (
    <div className="wi-header">
      <div>
        {/* Eyebrow row */}
        <div className="wi-x257">
          <span className="wi-x258">
            Strategy Intelligence
          </span>
          {anyRuleActive && (
            <>
              <span className="kfx-active-dot"/>
              <span className="wi-x259">
                {activeRuleCount} rule{activeRuleCount !== 1 ? 's' : ''} active
              </span>
            </>
          )}
        </div>

        {/* Page title */}
        <div className="h2 wi-x260">
          What-If <span className="txt-accent">Simulator</span>
        </div>

        {/* Account context + upgrade nudge */}
        <div className="wi-x261">
          {activeAccount?.name && (
            <span className="wi-x262">
              {activeAccount.name}
            </span>
          )}
          {activeAccount?.name && (
            <span className="wi-x263">·</span>
          )}
          <span className="wi-x264">
            {trades.length} trades analysed
          </span>
          {userPlan === 'free' && (
            <>
              <span className="wi-x265">·</span>
              <button onClick={onUpgrade} className="wi-x266"
              onMouseEnter={e => e.currentTarget.style.opacity = '.75'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
              >
                Upgrade to unlock full analytics ↗
              </button>
            </>
          )}
        </div>
      </div>
      {/* Action buttons */}
      <div className="wi-x267">
        <button onClick={() => setCustomizeOpen(true)} className="wi-customize-btn">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="20" y2="12"/><line x1="4" y1="18" x2="20" y2="18"/>
            <circle cx="9" cy="6" r="3" fill="var(--dark2)"/><circle cx="15" cy="12" r="3" fill="var(--dark2)"/><circle cx="9" cy="18" r="3" fill="var(--dark2)"/>
          </svg>
          CUSTOMIZE
          {hiddenCount > 0 && (
            <span className="wi-x268">
              {hiddenCount} hidden
            </span>
          )}
        </button>
        <button onClick={() => { if (dna) setDnaOpen(true); }} className="wi-dna-btn"
          disabled={!dna} title={!dna ? `Log at least 5 trades to unlock Trader DNA (${trades.length}/5)` : undefined}
          style={{ opacity: dna ? 1 : 0.4, cursor: dna ? 'pointer' : 'not-allowed' }}
          onMouseEnter={e => { if (dna) e.currentTarget.style.background = 'var(--wi-gold-200)'; }}
          onMouseLeave={e => { if (dna) e.currentTarget.style.background = 'linear-gradient(135deg,var(--wi-gold-180),var(--wi-gold-50))'; }}>
          <span className="wi-x269">{dna?.icon || '🧬'}</span>
          VIEW TRADER DNA
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   INTELLIGENCE SUMMARY CARD  (free-user hook above widgets)
───────────────────────────────────────────────────────────────────────────── */
function IntelligenceSummaryCard({ trades, onUpgrade, monthlyReturn }) {
  const positive = monthlyReturn >= 0;

  // Real count of pair/session/strategy combos outperforming the account's
  // overall avg P&L-per-trade by 2x+ (mirrors BestSetupFinder's grouping logic)
  const { realSetupCount, hasLosingWindow } = useMemo(() => {
    if (!trades.length) return { realSetupCount: 0, hasLosingWindow: false };

    const overallAvg = trades.reduce((a, t) => a + Number(t.result), 0) / trades.length;

    const map = {};
    trades.forEach(t => {
      const keys = [t.pair || null, t.session || null, t.strat || null,
        (t.pair && t.session) ? `${t.pair} + ${t.session}` : null,
        (t.pair && t.strat)   ? `${t.pair} + ${t.strat}`   : null,
        (t.session && t.strat)? `${t.session} + ${t.strat}` : null,
      ].filter(Boolean);
      keys.forEach(key => {
        if (!map[key]) map[key] = { n:0, pnl:0 };
        map[key].n++; map[key].pnl += Number(t.result);
      });
    });
    const realSetupCount = Object.values(map)
      .filter(d => d.n >= 3)
      .filter(d => {
        const avg = d.pnl / d.n;
        return overallAvg > 0 ? avg >= overallAvg * 2 : avg > 0 && overallAvg <= 0;
      }).length;

    // Has at least one hour with 3+ trades and a net-negative result
    const hourPnl = {};
    trades.forEach(t => {
      if (!t.time) return;
      const h = parseInt(t.time.split(':')[0], 10);
      if (isNaN(h)) return;
      if (!hourPnl[h]) hourPnl[h] = { n:0, pnl:0 };
      hourPnl[h].n++; hourPnl[h].pnl += Number(t.result);
    });
    const hasLosingWindow = Object.values(hourPnl).some(h => h.n >= 3 && h.pnl < 0);

    return { realSetupCount, hasLosingWindow };
  }, [trades]);

  const items = [
    {
      icon: '🎯',
      text: realSetupCount > 0
        ? `${realSetupCount} setup combination${realSetupCount !== 1 ? 's' : ''} ${realSetupCount !== 1 ? 'are' : 'is'} outperforming your average by 2×+ in expected value`
        : 'Run the simulator to surface setup combinations that outperform your average',
    },
    {
      icon: '⏰',
      text: hasLosingWindow
        ? 'One 2-hour window accounts for a disproportionate share of your losses — most traders never find it'
        : 'No major loss-concentrated trading window detected yet — keep logging entry times to refine this',
    },
    {
      icon: '📈',
      text: positive
        ? `Your account is on a positive trajectory at your current monthly edge (+${monthlyReturn.toFixed(1)}%)`
        : `Your current monthly return is negative — use the simulator to diagnose the cause`,
    },
  ];

  return (
    <div className="kfx-intelligence-card">
      <div className="wi-x270">
        <span className="wi-x271">
          ◈ INTELLIGENCE REPORT
        </span>
        <span className="wi-x272">
          · {trades.length} trades analysed
        </span>
      </div>
      <div className="wi-x273">
        We found patterns in your data that most traders overlook.
      </div>
      <div className="wi-x274">
        {items.map((item, i) => (
          <div key={i} className="wi-x275">
            <span className="wi-x276">{item.icon}</span>
            <span className="wi-x277">
              {item.text}
            </span>
          </div>
        ))}
      </div>
      <button onClick={onUpgrade} className="wi-x278"
      onMouseEnter={e => { e.currentTarget.style.transform='translateY(-1px)'; e.currentTarget.style.boxShadow='0 6px 20px var(--wi-gold-380)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow='0 4px 16px var(--wi-gold-250)'; }}
      >
        SEE FULL INTELLIGENCE REPORT →
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   HERO KPI SECTION  (replaces flat wi-kpis grid for unlocked users)
───────────────────────────────────────────────────────────────────────────── */
function HeroKpiSection({ kpis, diff, diffPct, activeRuleCount = 0 }) {
  const noRulesActive = activeRuleCount === 0;
  return (
    <>
      {/* ── Hero card: Rule Impact ── */}
      <div className="kfx-hero-kpi">
        <div className="wi-x279">
          <div className="wi-x280">
            RULE IMPACT
          </div>
          <span className="wi-x281">
            {noRulesActive ? 'toggle a filter to simulate' : 'vs your unfiltered baseline'}
          </span>
        </div>


        {/* The number */}
        <div className="wi-x282">
          <div
            className="wi-x283"
            style={{
              color: noRulesActive ? 'var(--wi-text-350)' : diff >= 0 ? 'var(--gold)' : 'var(--red)'
            }}>
            {noRulesActive ? '—' : fmt$(diff)}
          </div>
          {!noRulesActive && kpis.actualFinal !== 0 && Math.abs(diffPct) > 0.5 && (
            <div className="wi-x284">
              {diffPct > 0 ? '+' : ''}{diffPct.toFixed(1)}% vs actual
            </div>
          )}
          {noRulesActive && (
            <div className="wi-x285">
              No filters applied — simulated equals actual
            </div>
          )}
        </div>

        {/* Ratio bar */}
        <div className="kfx-diff-bar">
          <div className="kfx-diff-bar-fill" style={{
            width: noRulesActive ? '0%' : `${Math.min(100, Math.max(5,
              kpis.actualFinal !== 0
                ? Math.abs(kpis.hypFinal / (Math.abs(kpis.actualFinal) || 1)) * 50
                : 50
            ))}%`,
            background: diff >= 0 ? 'var(--gold)' : 'var(--red)',
          }}/>
        </div>
      </div>
      {/* ── Secondary KPIs: 3-column ── */}
      <div className="wi-x286">
        {[
          {
            label:'Actual P&L',
            val:fmt$(kpis.actualFinal),
            color: kpis.actualFinal >= 0 ? 'var(--green)' : 'var(--red)'
          },
          {
            label:'Simulated P&L',
            val:fmt$(kpis.hypFinal),
            color: kpis.hypFinal >= 0 ? 'var(--green)' : 'var(--red)'
          },
          {
            label:'Filtered Out',
            val:`${kpis.skipped} trades`,
            color:'var(--gold)',
            sub: kpis.skipped > 0 ? 'that would have hurt' : null
          },
        ].map(({ label, val, color, sub }) => (
          <div key={label} className="wi-x287">
            <div className="wi-x288">
              {label}
            </div>
            <div
              className="wi-x289"
              style={{
                color
              }}>
              {val}
            </div>
            {sub && (
              <div className="wi-x290">
                {sub}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   STRATEGY BLUEPRINT TEASER  (replaces SimulatorControlsTeaser)
───────────────────────────────────────────────────────────────────────────── */
function SimulatorControlsTeaserV2({ onUpgrade }) {
  const blueprintRules = [
    { label:'No Revenge Trades', impact:'+$920 avg/mo', color:'var(--wi-red-800)', dot:'#D94F4F' },
    { label:'Best Session Only', impact:'+$1,240 avg/mo', color:'var(--wi-blue2-800)', dot:'#3B82F6' },
    { label:'Daily Loss Cap: 3',  impact:'+$380 avg/mo',  color:'var(--wi-green-700)', dot:'#3DBE73' },
  ];

  return (
    <div className="wi-controls">
      {/* Header */}
      <div className="wi-x291">
        <div className="wi-x292">
          STRATEGY BLUEPRINT
        </div>
        <span className="wi-x293">
          EXAMPLE
        </span>
      </div>
      {/* Intro text */}
      <div className="wi-x294">
        Accounts with similar trade history typically find these rules significant:
      </div>
      {/* Blueprint rule rows */}
      <div className="wi-x295">
        {blueprintRules.map((r, i) => (
          <div key={i} className="kfx-blueprint-rule">
            <div
              className="wi-x296"
              style={{
                background:r.dot,
                boxShadow:`0 0 5px ${r.dot}55`
              }} />
            <span className="wi-x297">
              {r.label}
            </span>
            <span
              className="wi-x298"
              style={{
                color:r.color
              }}>
              {r.impact}
            </span>
          </div>
        ))}
      </div>
      {/* Divider */}
      <div className="wi-x299"/>
      {/* Upgrade panel */}
      <div className="wi-x300">
        <div className="wi-x301">
          Run your own simulation
        </div>
        <div className="wi-x302">
          12 toggleable rules. Instant equity curve recalculation.
          See the dollar value of every discipline decision.
        </div>
        <button onClick={onUpgrade} className="wi-x303"
        onMouseEnter={e => { e.currentTarget.style.transform='translateY(-1px)'; e.currentTarget.style.boxShadow='0 6px 20px var(--wi-gold-350)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow='none'; }}
        >
          UNLOCK RULES ENGINE →
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   SETUP RANK ROW — tiered treatment for Best Setup Finder rows
───────────────────────────────────────────────────────────────────────────── */
function SetupRankRow({ c, i, metric, maxPnl, maxScore }) {
  const barPct =
    metric === 'wr'    ? c.wr :
    metric === 'pnl'   ? Math.abs(c.pnl) / maxPnl * 100 :
    metric === 'rr'    ? Math.min(100, c.avgRR / 4 * 100) :
    Math.max(4, c.score / (maxScore || 1) * 100);

  const barColor =
    metric === 'wr'  ? (c.wr >= 50 ? 'var(--green)' : 'var(--red)') :
    metric === 'pnl' ? (c.pnl >= 0 ? 'var(--green)' : 'var(--red)') :
    metric === 'rr'  ? 'var(--blue)' : 'var(--gold)';

  const valLabel =
    metric === 'wr'  ? `${c.wr}%` :
    metric === 'pnl' ? fmt$(c.pnl) :
    metric === 'rr'  ? `${c.avgRR.toFixed(2)}R` :
    c.score.toFixed(2);

  const isElite = i === 0;
  const isStrong = i > 0 && i < 3 && c.score >= 1.0;

  const tierBadge = isElite
    ? { cls:'kfx-rank-elite', text:'◈ ELITE' }
    : isStrong
    ? { cls:'kfx-rank-strong', text:'▲ STRONG' }
    : i < 5
    ? { cls:'kfx-rank-std', text:'STD' }
    : null;

  const barH = isElite ? 8 : i < 3 ? 6 : 5;
  const numColor = isElite ? 'var(--gold)' : i < 3 ? 'var(--wi-text-700)' : 'var(--wi-text-300)';
  const numSize = isElite ? 14 : 11;

  return (
    <div className={isElite ? 'kfx-setup-elite-row' : 'kfx-setup-row'}>
      {/* Top row: rank + label + tier badge */}
      <div className="wi-x304">
        <span
          className="wi-x305"
          style={{
            fontSize:numSize,
            color:numColor
          }}>
          #{i+1}
        </span>
        <span
          className="wi-x306"
          style={{
            fontWeight:isElite ? 600 : 500,
            color: isElite ? 'var(--off-white, #eaeaf2)' : 'var(--wi-text-750)'
          }}>
          {c.label}
        </span>
        {tierBadge && (
          <span className={tierBadge.cls}>{tierBadge.text}</span>
        )}
      </div>
      {/* Bar row */}
      <div className="wi-x307">
        <div
          className="wi-x308"
          style={{
            height:barH,
            borderRadius:barH
          }}>
          <div
            className="wi-x309"
            style={{
              width:`${barPct}%`,
              background:barColor,
              borderRadius:barH
            }} />
        </div>
        <span
          className="wi-x310"
          style={{
            color:barColor
          }}>
          {valLabel}
        </span>
        <span className="wi-x311">
          {c.n}t
        </span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   ACTIVE RULES SUMMARY STRIP
───────────────────────────────────────────────────────────────────────────── */
function ActiveRulesSummary({ activeRuleCount, diff, isPositive }) {
  if (activeRuleCount === 0) return null;

  return (
    <div
      className="wi-x312"
      style={{
        background: isPositive ? 'var(--wi-green-60)' : 'var(--wi-red-60)',
        border:`1px solid ${isPositive ? 'var(--wi-green-150)' : 'var(--wi-red-150)'}`
      }}>
      <span
        className="wi-x313"
        style={{
          color: isPositive ? 'var(--green)' : 'var(--red)'
        }}>
        {isPositive ? '↑' : '↓'}
      </span>
      <span
        className="wi-x314"
        style={{
          color: isPositive ? 'var(--green)' : 'var(--red)'
        }}>
        {activeRuleCount} rule{activeRuleCount !== 1 ? 's' : ''} active
      </span>
      <span className="wi-x315">·</span>
      <span className="wi-x316">
        {fmt$(diff)} simulated impact
      </span>
    </div>
  );
}

/* TEASER — EQUITY CURVE */
function EquityCurveTeaser() {
  const demoActual = [0,-120,-340,-180,-520,-410,-700,-580,-820,-1050,-970,-1100,-1060,-1240];
  const demoHypo   = [0,120,280,420,640,860,1100,1380,1720,2100,2480,2910,3380,3860];
  return (
    <Panel>
      <div className="wi-x317">
        <div>
          <div className="h5 wi-x318">Equity Curve Comparison</div>
          <div className="txt-caption">Hypothetical vs Actual — real-time recalculation</div>
        </div>
        <div className="wi-x319">
          {[['var(--red)','Actual'],['#3DBE73','Simulated']].map(([c,l]) => (
            <div key={l} className="txt-caption wi-x320">
              <div
                className="wi-x321"
                style={{
                  background:c
                }} />{l}
            </div>
          ))}
        </div>
      </div>
      <div className="wi-x322">
        <EquityCanvas actual={demoActual} hypothetical={demoHypo}/>
      </div>
    </Panel>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────────────────────────────────────────── */
export default function WhatIfPage({ trades = [], user, activeAccount, tradingAccounts = [], onUpgrade }) {
  const userPlan = user?.plan ?? 'free';
  const { requiredPlans, featureLabel } = useFeatureConfig();

  // trades prop is already filtered to the active account by App.jsx — use it directly.
  // No internal re-filter needed; App.jsx owns account filtering for all pages.

  // Build gate props from feature key — passes through to <FeatureGate>
  const gate = (key) => ({
    userPlan,
    requiredPlans: requiredPlans(key),
    label: featureLabel(key),
    onUpgrade,
  });

  // ── Simulator state ────────────────────────────────────────────────────────
  const [noFridays,       setNoFridays]       = useState(false);
  const [noMondays,       setNoMondays]       = useState(false);
  const [noNewsDay,       setNoNewsDay]       = useState(false);
  const [noRevenge,       setNoRevenge]       = useState(false);
  const [moveToBreakEven, setMoveToBreakEven] = useState(false);
  const [breakEvenAt,     setBreakEvenAt]     = useState(1.0);
  const [capLoss,         setCapLoss]         = useState(false);
  const [maxLossCap,      setMaxLossCap]      = useState(() => {
    const lossesByDay = {};
    trades.forEach(t => { if (t.result < 0) lossesByDay[t.date] = (lossesByDay[t.date] || 0) + 1; });
    const counts = Object.values(lossesByDay);
    if (!counts.length) return 3;
    return Math.max(1, Math.min(5, Math.round(counts.reduce((a, v) => a + v, 0) / counts.length)));
  });
  const [minRR,           setMinRR]           = useState(false);
  const [minRRVal,        setMinRRVal]        = useState(() => {
    const rrVals = trades.map(t => parseFloat(t.actualRR || t.rr)).filter(v => v > 0).sort((a, b) => a - b);
    if (!rrVals.length) return 1.5;
    return Math.max(0.5, Math.round(rrVals[Math.floor(rrVals.length / 2)] * 2) / 2);
  });
  const [onlySession,     setOnlySession]     = useState(false);
  const [bestSession,     setBestSession]     = useState(() => {
    const pnlBySession = {};
    trades.forEach(t => { const k = t.session || 'Unknown'; pnlBySession[k] = (pnlBySession[k] || 0) + t.result; });
    const best = Object.entries(pnlBySession).sort((a, b) => b[1] - a[1])[0];
    return best ? best[0] : 'London';
  });
  const [ruleOnly,        setRuleOnly]        = useState(false);
  const [lotScale,        setLotScale]        = useState(100);

  // ── Active rule count (for header pulse + summary strip) ──────────────────
  const activeRuleCount = [
    noFridays, noMondays, noNewsDay, noRevenge,
    moveToBreakEven, capLoss, minRR, onlySession, ruleOnly,
    lotScale !== 100,
  ].filter(Boolean).length;
  const anyRuleActive = activeRuleCount > 0;

  // ── Avg monthly return (used by Intelligence Summary Card for free users) ──
  const monthlyReturn = useMemo(() => {
    if (!trades.length) return 0;
    const byMonth = {};
    trades.forEach(t => {
      const key = (t.date || '').slice(0, 7);
      if (key) byMonth[key] = (byMonth[key] || 0) + Number(t.result);
    });
    const vals = Object.values(byMonth);
    if (!vals.length) return 0;
    const startBalance = Math.max(100, parseFloat(activeAccount?.balance) || 10_000);
    return (vals.reduce((a, v) => a + v, 0) / vals.length) / startBalance * 100;
  }, [trades, activeAccount?.balance]);

  // ── UI state ──────────────────────────────────────────────────────────────
  const [dnaOpen,         setDnaOpen]         = useState(false);
  const [infoFilters,     setInfoFilters]     = useState(false);
  const [infoKpis,        setInfoKpis]        = useState(false);
  const [infoCurve,       setInfoCurve]       = useState(false);
  const [infoMilestones,  setInfoMilestones]  = useState(false);
  const [customizeOpen,   setCustomizeOpen]   = useState(false);

  // ── Widget visibility ─────────────────────────────────────────────────────
  const WIDGET_DEFS = [
    { id:'filters',    label:'Simulator Filters',     icon:'🎛️', desc:'Session, day, entry & risk controls' },
    { id:'kpis',       label:'KPI Cards',              icon:'📊', desc:'Actual P&L, Simulated P&L, Difference' },
    { id:'curve',      label:'Equity Curve',           icon:'📈', desc:'Visual comparison of actual vs simulated' },
    { id:'milestones', label:'Milestones',             icon:'🏆', desc:'Achievement progress tracker' },
    { id:'compound',   label:'Compound Projector',     icon:'💹', desc:'Project your performance forward' },
    { id:'setups',     label:'Best Setup Finder',      icon:'🎯', desc:'Your top pair/session/strategy combos' },
    { id:'heatmap',    label:'Timing Heatmap',         icon:'⏰', desc:'Best and worst trading hours' },
    { id:'sizing',     label:'Position Sizing Impact', icon:'⚖️', desc:'Risk % effect on results' },
  ];
  const DEFAULT_VISIBLE = WIDGET_DEFS.reduce((acc, w) => ({ ...acc, [w.id]: true }), {});
  const [visible, setVisible] = useState(() => {
    try { return { ...DEFAULT_VISIBLE, ...JSON.parse(localStorage.getItem('wi_widget_visibility') || '{}') }; }
    catch { return DEFAULT_VISIBLE; }
  });
  const [customizeOpen2, setCustomizeOpen2] = useState(false); // alias kept for modal
  const toggleWidget = id => {
    setVisible(prev => {
      const next = { ...prev, [id]: !prev[id] };
      try { localStorage.setItem('wi_widget_visibility', JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const hiddenCount = Object.values(visible).filter(v => !v).length;

  // ── Derived slider bounds ─────────────────────────────────────────────────
  const maxRRInData = useMemo(() =>
    Math.max(4, Math.ceil(trades.reduce((a, t) => Math.max(a, parseFloat(t.actualRR || t.rr) || 0), 0))), [trades]);
  const maxLossCapBound = useMemo(() => {
    const lossesByDay = {};
    trades.forEach(t => { if (Number(t.result) < 0) lossesByDay[t.date] = (lossesByDay[t.date] || 0) + 1; });
    return Math.max(5, ...Object.values(lossesByDay));
  }, [trades]);
  const sessionStats = useMemo(() => {
    const m = {};
    trades.forEach(t => {
      const k = t.session || 'Unknown';
      if (!m[k]) m[k] = { n:0, wins:0, pnl:0 };
      m[k].n++; m[k].pnl += t.result; if (t.result > 0) m[k].wins++;
    });
    return Object.entries(m).sort((a, b) => b[1].pnl - a[1].pnl);
  }, [trades]);

  // ── Simulation ────────────────────────────────────────────────────────────
  const { actual, hypothetical, diff, diffPct, kpis } = useMemo(() => {
    const sorted = [...trades].sort((a, b) => {
      const d = a.date.localeCompare(b.date); if (d !== 0) return d;
      return (Number(a.id) || 0) - (Number(b.id) || 0);
    });
    let eq = 0;
    const actual = sorted.map(t => { eq += Number(t.result); return eq; });
    const dayLossCount = {};
    let lastWasLoss = false;
    const simTrades = sorted.map(t => {
      const [y, mo, d] = t.date.split('-');
      const dow = new Date(Date.UTC(y, mo - 1, d)).getUTCDay();
      const rr  = parseFloat(t.actualRR || t.rr) || 0;
      const dayKey = t.date;
      if (!dayLossCount[dayKey]) dayLossCount[dayKey] = 0;
      let skip = false, result = Number(t.result);
      if (noFridays && dow === 5) skip = true;
      if (noMondays && dow === 1) skip = true;
      if (noNewsDay && t.newsPresent) skip = true;
      if (noRevenge && lastWasLoss) skip = true;
      if (onlySession && t.session !== bestSession) skip = true;
      if (minRR && (rr <= 0 || rr < minRRVal)) skip = true;
      if (ruleOnly) {
        const checks = t.ruleChecks || [];
        const followed = checks.length > 0 ? checks.every(Boolean) : (t.followedPlan === true);
        if (!followed) skip = true;
      }
      if (capLoss && !skip && Number(t.result) < 0) {
        if (dayLossCount[dayKey] >= maxLossCap) skip = true;
        else dayLossCount[dayKey]++;
      }
      const riskUnit = (parseFloat(t.riskAmount) > 0) ? parseFloat(t.riskAmount) : (rr > 0 ? Math.abs(t.result / rr) : 0);
      const actualR  = riskUnit > 0 ? t.result / riskUnit : rr;
      if (moveToBreakEven && !skip && t.result > 0 && actualR >= breakEvenAt) result = 0;
      if (!skip && lotScale !== 100) result = result * (lotScale / 100);
      if (!skip) lastWasLoss = Number(t.result) < 0;
      return { skip, result };
    });
    let hyp = 0;
    const hypothetical = simTrades.map(s => { if (!s.skip) hyp += s.result; return hyp; });
    const actualFinal  = actual[actual.length - 1] || 0;
    const hypFinal     = hypothetical[hypothetical.length - 1] || 0;
    const diff         = hypFinal - actualFinal;
    const rawDiffPct   = actualFinal !== 0 ? (diff / Math.abs(actualFinal)) * 100 : 0;
    const diffPct      = Math.max(-999, Math.min(999, rawDiffPct));
    const skipped      = simTrades.filter(s => s.skip).length;
    const kept         = simTrades.length - skipped;
    return { actual, hypothetical, diff, diffPct, kpis: { skipped, kept, actualFinal, hypFinal, ruleOnlyWipeout: ruleOnly && kept === 0 } };
  }, [trades, noFridays, noMondays, noNewsDay, noRevenge, moveToBreakEven, breakEvenAt, capLoss, maxLossCap, minRR, minRRVal, onlySession, bestSession, ruleOnly, lotScale]);

  // ── Trader DNA ────────────────────────────────────────────────────────────
  const dna = useMemo(() => {
    if (trades.length < 5) return null;
    const wins = trades.filter(t => Number(t.result) > 0);
    const wr   = wins.length / trades.length;
    const rrTrades = trades.filter(t => parseFloat(t.actualRR || t.rr) > 0);
    const avgRR = rrTrades.reduce((a, t) => a + parseFloat(t.actualRR || t.rr), 0) / (rrTrades.length || 1);
    if (wr >= 0.65 && avgRR >= 2.0) return { class:'THE MACHINE',    icon:'⚙️', color:'#4E7FD4', wr, avgRR, desc:"Elite execution. High win rate AND strong R:R. You're the market's most feared opponent — don't let overconfidence become your kryptonite." };
    if (avgRR >= 2.5 && wr < 0.55)  return { class:'THE SNIPER',     icon:'🎯', color:'#3DBE73', wr, avgRR, desc:"Low frequency, high reward. You wait patiently for the perfect setup. Your edge is patience — protect it." };
    if (wr >= 0.65 && avgRR < 1.5)  return { class:'THE SCALPER',    icon:'⚡', color:'#C9A84C', wr, avgRR, desc:"High win rate but leaving money on the table. Try extending TP targets by 20% to unlock your next level." };
    if (wr < 0.45 && avgRR >= 2.0)  return { class:'THE CONTRARIAN', icon:'🔄', color:'#E2A542', wr, avgRR, desc:"You lose often but recover big when right. A deadly style — build a hard daily loss limit." };
    if (wr < 0.45 && avgRR < 1.5)   return { class:'THE LEARNER',    icon:'📚', color:'#D94F4F', wr, avgRR, desc:"Still developing your edge. Focus on one strategy, one session, one pair." };
    return                                   { class:'THE BALANCED',  icon:'⚖️', color:'#A89F8C', wr, avgRR, desc:"Solid, consistent trading. Identify your single best session or strategy and double down on it." };
  }, [trades]);

  // ── Milestones ────────────────────────────────────────────────────────────
  const milestones = useMemo(() => {
    const ruleAdherent = trades.filter(t => {
      const c = t.ruleChecks || [];
      return c.length > 0 ? c.every(Boolean) : t.followedPlan === true;
    }).length;
    const thirtyDaysAgoMs = Date.now() - 30 * 86400000;
    const recentTradeDays = new Set(
      trades.filter(t => { const [ty, tm, td] = t.date.split('-'); return Date.UTC(ty, tm - 1, td) >= thirtyDaysAgoMs; })
            .map(t => t.date)
    ).size;
    const streakCurrent = Math.min(30, recentTradeDays);
    return [
      { label:'First 10 Trades',       target:10,  current:trades.length, reward:'Unlock Analytics Page',       done: trades.length >= 10 },
      { label:'50 Disciplined Trades',  target:50,  current:ruleAdherent,  reward:'Unlock Deep Strategy Report', done: ruleAdherent >= 50 },
      { label:'100 Total Trades',       target:100, current:trades.length, reward:'Unlock Trader DNA Profile',   done: trades.length >= 100 },
      { label:'30-Day Streak',          target:30,  current:streakCurrent, reward:'Unlock What-If Advanced Mode',done: streakCurrent >= 30 },
    ];
  }, [trades]);

  // ── Empty state ───────────────────────────────────────────────────────────
  if (trades.length < 3) {
    return (
      <div className="wi-x323">
        <div className="wi-x324">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="1.6"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/></svg>
        </div>
        <div className="h3">Need more trades</div>
        <div className="txt-body-sm txt-muted wi-x325">Log at least 3 trades to start using the What-If Simulator.</div>
      </div>
    );
  }

  /* ──────────────────────────────────────────────────────────────────────────
     RENDER
  ────────────────────────────────────────────────────────────────────────── */
  return (
    <div className="wi-page">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <WhatIfPageHeader
        trades={trades}
        user={user}
        activeAccount={activeAccount}
        onUpgrade={onUpgrade}
        anyRuleActive={anyRuleActive}
        activeRuleCount={activeRuleCount}
        dna={dna}
        setDnaOpen={setDnaOpen}
        setCustomizeOpen={setCustomizeOpen}
        hiddenCount={hiddenCount}
      />
      {/* ── Intelligence Summary Card (free-user hook) ───────────────────────── */}
      {userPlan === 'free' && (
        <IntelligenceSummaryCard trades={trades} onUpgrade={onUpgrade} monthlyReturn={monthlyReturn}/>
      )}
      <div className="wi-body">

        {/* ══════════════════════════════════════════════════════════════════
            LEFT COLUMN — SIMULATOR CONTROLS
        ══════════════════════════════════════════════════════════════════ */}
        {visible.filters && (
        <IntelligenceGate {...gate('whatif_filters')} featureKey="filters"
          headline="Run Your Own Simulation" ctaLabel="Unlock Rules Engine"
          ctaSubline="12 toggleable rules" revealHeight={320} surfaceColor="var(--dark2, #111113)">
          {!requiredPlans('whatif_filters').includes(userPlan)
            ? <SimulatorControlsTeaserV2 onUpgrade={onUpgrade}/>
            : <div className="wi-controls">
            <div className="wi-x326">
              <div className="txt-micro wi-x327">SIMULATOR FILTERS</div>
              <InfoIcon onClick={() => setInfoFilters(true)}/>
            </div>

            <Panel>
              <SecLabel>Session & Day Filters</SecLabel>
              <Toggle on={noFridays}  onChange={setNoFridays}  label="Skip Fridays"  sub="Excludes all Friday trades"/>
              <Toggle on={noMondays}  onChange={setNoMondays}  label="Skip Mondays"  sub="Excludes all Monday trades"/>
              <Toggle on={noNewsDay}  onChange={setNoNewsDay}  label="Skip News Days" sub="Excludes trades on days flagged with high-impact news" color="var(--red)"/>
              <Toggle on={onlySession} onChange={setOnlySession}
                label={`Only "${bestSession}" session`} sub="Skips all other sessions" color="var(--blue)"/>
              {onlySession && (
                <div className="wi-x328">
                  <select value={bestSession} onChange={e => setBestSession(e.target.value)}
                    className="wi-x329">
                    {sessionStats.map(([ses, d]) => (
                      <option key={ses} value={ses}>{ses} ({d.n} trades, {fmt$(d.pnl)})</option>
                    ))}
                  </select>
                </div>
              )}
            </Panel>

            <Panel>
              <SecLabel>Entry Rules</SecLabel>
              <Toggle on={noRevenge} onChange={setNoRevenge} label="No Revenge Trades" sub="Skip any trade immediately after a loss" color="var(--red)"/>
              <Toggle on={minRR}     onChange={setMinRR}     label="Min R:R Filter"    sub="Only take trades with R:R above threshold" color="var(--green)"/>
              {minRR && <Slider label="Minimum R:R" value={minRRVal} min={0.5} max={maxRRInData} step={0.5} format={v => `${v}R`} onChange={setMinRRVal}/>}
              <Toggle on={ruleOnly}  onChange={setRuleOnly}  label="Rule-Adherent Only" sub="Only trades where all rule-checks are marked" color="var(--gold)"/>
            </Panel>

            <Panel>
              <SecLabel>Risk Management</SecLabel>
              <Toggle on={capLoss} onChange={setCapLoss} label="Daily Loss Cap" sub="Stop trading after N losses per day" color="var(--red)"/>
              {capLoss && <Slider label="Max losses/day" value={maxLossCap} min={1} max={maxLossCapBound} step={1} onChange={setMaxLossCap} color="var(--red)"/>}
              <Toggle on={moveToBreakEven} onChange={setMoveToBreakEven} label="Move SL to Break-Even" sub="Converts winning trades near BE trigger to 0" color="var(--green)"/>
              {moveToBreakEven && <Slider label="Move SL at R" value={breakEvenAt} min={0.5} max={2} step={0.25} format={v => `${v}R`} onChange={setBreakEvenAt}/>}
            </Panel>
          </div>
          }
        </IntelligenceGate>
        )}

        {/* ══════════════════════════════════════════════════════════════════
            RIGHT COLUMN — RESULTS + WIDGETS
        ══════════════════════════════════════════════════════════════════ */}
        <div className="wi-results">

          {/* ── KPI Cards ───────────────────────────────────────────────── */}
          {visible.kpis && (
          <IntelligenceGate {...gate('whatif_kpis')} featureKey="kpis"
            headline="See Every Rule's Dollar Impact" ctaLabel="See KPI Breakdown"
            revealHeight={170} surfaceColor="var(--dark1, #0A0A0C)">
            {!requiredPlans('whatif_kpis').includes(userPlan)
              ? <KpiCardsTeaser/>
              : <>
                <div className="wi-x330">
                  <div className="txt-micro wi-x331">SIMULATION RESULTS</div>
                  <InfoIcon onClick={() => setInfoKpis(true)}/>
                </div>
                <InfoPanel open={infoKpis} onClose={() => setInfoKpis(false)} title="KPI Cards" icon="📊">
                  <InfoSection heading="THE FOUR METRICS">
                    <strong>Actual P&L</strong> — your real total from all logged trades.<br/><br/>
                    <strong>Simulated P&L</strong> — what you would have made with active rules applied.<br/><br/>
                    <strong>Difference</strong> — Simulated minus Actual.<br/><br/>
                    <strong>Trades Skipped</strong> — how many trades were filtered out.
                  </InfoSection>
                  <InfoTip>The % figure under Difference shows improvement relative to your actual P&L.</InfoTip>
                </InfoPanel>
                <ActiveRulesSummary activeRuleCount={activeRuleCount} diff={diff} isPositive={diff >= 0}/>
                <HeroKpiSection kpis={kpis} diff={diff} diffPct={diffPct} activeRuleCount={activeRuleCount}/>
                {kpis.skipped > 0 && (
                  <div className="txt-micro txt-muted wi-x332">
                    {kpis.skipped} / {trades.length} trades skipped by active rules.
                  </div>
                )}
              </>
            }
          </IntelligenceGate>
          )}

          {/* ── Equity Curve ─────────────────────────────────────────────── */}
          {visible.curve && (
          <IntelligenceGate {...gate('whatif_curve')} featureKey="curve"
            headline="Visualize Your Rule-Adjusted Equity Curve" ctaLabel="See Equity Curve"
            revealHeight={260} surfaceColor="var(--dark2, #111113)">
            {!requiredPlans('whatif_curve').includes(userPlan)
              ? <EquityCurveTeaser/>
              : <Panel>
                <div className="wi-x333">
                  <div>
                    <div className="h5 wi-x334">Equity Curve Comparison</div>
                    <div className="txt-caption">Hypothetical vs Actual — real-time recalculation</div>
                  </div>
                  <div className="wi-x335">
                    <div className="wi-x336">
                      {[['var(--red)','Actual'],['#3DBE73','Simulated']].map(([c, l]) => (
                        <div key={l} className="txt-caption wi-x337">
                          <div
                            className="wi-x338"
                            style={{
                              background:c
                            }} />{l}
                        </div>
                      ))}
                    </div>
                    <InfoIcon onClick={() => setInfoCurve(true)}/>
                  </div>
                </div>
                <InfoPanel open={infoCurve} onClose={() => setInfoCurve(false)} title="Equity Curve Comparison" icon="📈">
                  <InfoSection heading="HOW TO READ IT">When the green line is above red, your rules improved performance. The gap at the right edge is your Difference KPI.</InfoSection>
                  <InfoTip>The most powerful use is spotting drawdown periods — if green avoids a red cliff, that rule is protecting your capital.</InfoTip>
                </InfoPanel>
                <div className="wi-x339">
                  <EquityCanvas actual={actual} hypothetical={hypothetical}/>
                </div>
              </Panel>
            }
          </IntelligenceGate>
          )}

          {/* Rule-Only wipeout warning */}
          {kpis.ruleOnlyWipeout && (
            <div className="wi-x340">
              <span className="wi-x341">⚠️</span>
              <div>
                <div className="txt-body-sm fw-bold txt-danger wi-x342">No rule-adherent trades found</div>
                <div className="txt-body-sm txt-muted wi-x343">
                  None of your trades have all rule-checks marked. Check that your trades have <code className="wi-x344">ruleChecks</code> or <code className="wi-x345">followedPlan</code> set before using this filter.
                </div>
              </div>
            </div>
          )}

          {/* Insight callout */}
          {Math.abs(diff) > Math.max(1, Math.abs(kpis.actualFinal) * 0.05) && (
            <div
              className="wi-x346"
              style={{
                background: diff > 0 ? 'var(--wi-green-70)' : 'var(--wi-red-70)',
                border:`1px solid ${diff > 0 ? 'var(--wi-green-200)' : 'var(--wi-red-200)'}`
              }}>
              <span className="wi-x347">{diff > 0 ? '💡' : '⚠️'}</span>
              <div>
                <div className="txt-body-sm fw-bold wi-x348" style={{
                  color:diff > 0 ? 'var(--green)' : 'var(--red)'
                }}>
                  {diff > 0 ? `These rules would have added ${fmt$(diff)} to your P&L` : `These rules would have cost you ${fmt$(diff)}`}
                </div>
                <div className="txt-body-sm txt-muted wi-x349">
                  {diff > 0
                    ? `By skipping ${kpis.skipped} trades that violated your rules, you would have kept ${fmt$(diff)} more.${kpis.actualFinal !== 0 ? ` That's ${diffPct.toFixed(1)}% improvement on your actual results.` : ''}`
                    : `Applying these rules would have reduced your P&L by ${fmt$(diff)} (${diffPct.toFixed(1)}% vs actual). Consider relaxing one of the filters.`
                  }
                </div>
              </div>
            </div>
          )}

          {/* ── Milestones ───────────────────────────────────────────────── */}
          {visible.milestones && (
          <IntelligenceGate {...gate('whatif_milestones')} featureKey="milestones"
            revealHeight={210} surfaceColor="var(--dark2, #111113)">
            {!requiredPlans('whatif_milestones').includes(userPlan)
              ? <GoalTrackerTeaser/>
              : <Panel>
              <div className="wi-x350">
                <div className="txt-micro wi-x351">MILESTONES & UNLOCKS</div>
                <InfoIcon onClick={() => setInfoMilestones(true)}/>
              </div>
              <InfoPanel open={infoMilestones} onClose={() => setInfoMilestones(false)} title="Milestones & Unlocks" icon="🏆">
                <InfoSection heading="WHAT IT DOES">Tracks your progress toward key trading consistency goals. Each milestone unlocks a feature when you hit the target.</InfoSection>
                <InfoTip>The Disciplined Trades milestone is the hardest and most valuable. Mark your rule-checks on every trade — honest data is more useful than clean data.</InfoTip>
              </InfoPanel>
              <div className="wi-milestones">
                {milestones.map(m => {
                  const pct = Math.min(100, Math.round(m.current / m.target * 100));
                  return (
                    <div
                      key={m.label}
                      className="wi-x352"
                      style={{
                        background: m.done ? 'var(--wi-green-60)' : 'var(--dark3)',
                        border:`1px solid ${m.done ? 'var(--wi-green-200)' : 'var(--wi-surface-50)'}`
                      }}>
                      {m.done && <div className="wi-x353">✅</div>}
                      <div className="txt-caption fw-semibold wi-x354" style={{
                        color:m.done ? 'var(--green)' : 'var(--wi-text-700)'
                      }}>{m.label}</div>
                      <div className="txt-micro txt-muted wi-x355">🔓 {m.reward}</div>
                      <div className="wi-progress-track-3">
                        <div
                          className="wi-x356"
                          style={{
                            width:`${pct}%`,
                            background:m.done ? 'var(--green)' : 'var(--gold)'
                          }} />
                      </div>
                      <div className="txt-micro txt-muted">{m.current} / {m.target}</div>
                    </div>
                  );
                })}
              </div>
            </Panel>
            }
          </IntelligenceGate>
          )}

          {/* ── ADVANCED ANALYSIS section divider ───────────────────────── */}
          <div className="wi-x357">
            <div className="wi-divider"/>
            <div className="wi-x358">ADVANCED ANALYSIS</div>
            <div className="wi-divider"/>
          </div>

          {/* ── Compound Projector ──────────────────────────────────────── */}
          {visible.compound && (
            <IntelligenceGate
              {...gate('whatif_compound')}
              featureKey="compound"
              revealHeight={310}
              ctaSubline="12-month projection locked"
            >
              {requiredPlans('whatif_compound').includes(userPlan)
                ? <CompoundProjector trades={trades} activeAccount={activeAccount}/>
                : <CompoundProjectorTeaser trades={trades} activeAccount={activeAccount}/>
              }
            </IntelligenceGate>
          )}

          {/* ── Best Setup Finder ───────────────────────────────────────── */}
          {visible.setups && (
            <IntelligenceGate
              {...gate('whatif_setups')}
              featureKey="setup"
              revealHeight={300}
              ctaSubline="2 more setups ranked & hidden"
            >
              {requiredPlans('whatif_setups').includes(userPlan)
                ? <BestSetupFinder trades={trades}/>
                : <BestSetupFinderTeaser/>
              }
            </IntelligenceGate>
          )}

          {/* ── Heatmap + Sizing side-by-side ───────────────────────────── */}
          {(visible.heatmap || visible.sizing) && (
            <div className="wi-widget-pair">
              {visible.heatmap && (
                <IntelligenceGate {...gate('whatif_heatmap')} featureKey="heatmap" revealHeight={280} ctaSubline="Worst hour + Avoid hour hidden">
                  {requiredPlans('whatif_heatmap').includes(userPlan)
                    ? <TimingHeatmap key={activeAccount?.id} trades={trades} activeAccount={activeAccount}/>
                    : <TimingHeatmapTeaser key={activeAccount?.id} trades={trades}/>
                  }
                </IntelligenceGate>
              )}
              {visible.sizing && (
                <IntelligenceGate {...gate('whatif_sizing')} featureKey="sizing" revealHeight={280} ctaSubline="Interactive slider + full analysis hidden">
                  {requiredPlans('whatif_sizing').includes(userPlan)
                    ? <RiskSizingWidget trades={trades} activeAccount={activeAccount}/>
                    : <RiskSizingTeaser trades={trades} activeAccount={activeAccount}/>
                  }
                </IntelligenceGate>
              )}
            </div>
          )}

          {/* ── Upgrade value-prop banner (shown only when at least one advanced feature is locked) ── */}
          {['whatif_compound','whatif_setups','whatif_heatmap','whatif_sizing'].some(
            key => !planCanAccess(userPlan, requiredPlans(key))
          ) && (
            <div className="wi-x359">
              <span className="wi-x360">💡</span>
              <div>
                <div className="txt-body-sm fw-bold wi-x361">Unlock the full What-If Simulator</div>
                <div className="txt-body-sm txt-muted wi-x362">
                  Compound Projector, Best Setup Finder, Timing Heatmap, and Position Sizing are all available on the Pro plan.{' '}
                  <button onClick={onUpgrade} className="wi-x363">Upgrade now →</button>
                </div>
              </div>
            </div>
          )}

        </div>{/* /wi-results */}
      </div>{/* /wi-body */}
      {/* ══════════════════════════════════════════════════════════════════════
          CUSTOMIZE MODAL
      ════════════════════════════════════════════════════════════════════════ */}
      {customizeOpen && (
        <div className="wi-dna-overlay" onClick={() => setCustomizeOpen(false)}>
          <div className="wi-customize-modal" onClick={e => e.stopPropagation()}>
            <div className="wi-x364">
              <div>
                <div className="h5 wi-x365">Customize Layout</div>
                <div className="txt-caption txt-muted">Choose which sections to show or hide</div>
              </div>
              <button onClick={() => setCustomizeOpen(false)}
                className="wi-x366">✕</button>
            </div>
            {hiddenCount > 0 && (
              <button onClick={() => { const all = WIDGET_DEFS.reduce((acc, w) => ({ ...acc, [w.id]:true }), {}); setVisible(all); try { localStorage.setItem('wi_widget_visibility', JSON.stringify(all)); } catch {} }}
                className="wi-x367">
                SHOW ALL SECTIONS
              </button>
            )}
            <div className="wi-x368">
              {WIDGET_DEFS.map(w => (
                <div
                  key={w.id}
                  onClick={() => toggleWidget(w.id)}
                  className="wi-x369"
                  style={{
                    background: visible[w.id] ? 'var(--wi-surface-30)' : 'var(--wi-surface-inactive)',
                    border:`1px solid ${visible[w.id] ? 'var(--wi-surface-60)' : 'var(--wi-surface-20)'}`
                  }}>
                  <span className="wi-x370">{w.icon}</span>
                  <div className="wi-x371">
                    <div className="txt-body-sm fw-semibold wi-x372" style={{
                      color: visible[w.id] ? 'var(--off-white)' : 'var(--wi-text-300)'
                    }}>{w.label}</div>
                    <div className="txt-micro txt-muted wi-x373">{w.desc}</div>
                  </div>
                  <div
                    className="wi-x374"
                    style={{
                      background: visible[w.id] ? 'var(--green)' : 'var(--wi-surface-100)'
                    }}>
                    <div
                      className="wi-x375"
                      style={{
                        left: visible[w.id] ? 19 : 3
                      }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="txt-micro txt-muted wi-x376">
              Your layout is saved automatically and persists across sessions.
            </div>
          </div>
        </div>
      )}
      {/* ══════════════════════════════════════════════════════════════════════
          SIMULATOR FILTERS INFO PANEL
      ════════════════════════════════════════════════════════════════════════ */}
      <InfoPanel open={infoFilters} onClose={() => setInfoFilters(false)} title="Simulator Filters" icon="🎛️">
        <InfoSection heading="WHAT IT DOES">
          Each toggle retroactively removes or modifies trades from your history and instantly recalculates your equity curve.
        </InfoSection>
        <InfoSection heading="SESSION & DAY FILTERS">
          <strong>Skip Fridays / Mondays</strong> — removes all trades on those days.<br/><br/>
          <strong>Skip News Days</strong> — removes all trades where the day was flagged as a high-impact news day.<br/><br/>
          <strong>Only [session]</strong> — keeps only trades from your selected session.
        </InfoSection>
        <InfoSection heading="ENTRY RULES">
          <strong>No Revenge Trades</strong> — skips any trade that immediately followed a losing trade.<br/><br/>
          <strong>Min R:R Filter</strong> — removes trades below your threshold.<br/><br/>
          <strong>Rule-Adherent Only</strong> — keeps only fully rule-checked trades.
        </InfoSection>
        <InfoSection heading="RISK MANAGEMENT">
          <strong>Daily Loss Cap</strong> — stops counting trades after N losses in a single day.<br/><br/>
          <strong>Move SL to Break-Even</strong> — converts profitable trades that hit your R trigger to $0.
        </InfoSection>
        <InfoTip>Start by toggling one rule at a time. The Difference KPI tells you instantly what each rule is worth in real money.</InfoTip>
      </InfoPanel>
      {/* ══════════════════════════════════════════════════════════════════════
          TRADER DNA MODAL
      ════════════════════════════════════════════════════════════════════════ */}
      {dnaOpen && dna && (
        <div className="wi-dna-overlay" onClick={() => setDnaOpen(false)} onKeyDown={e => { if (e.key === 'Escape') setDnaOpen(false); }}>
          <div className="wi-dna-modal wi-x377" onClick={e => e.stopPropagation()}>
            <div
              className="wi-x378"
              style={{
                background:`radial-gradient(circle,${dna.color}18 0%,transparent 70%)`
              }} />
            <div className="txt-micro wi-x379">TRADER DNA PROFILE</div>
            <div className="wi-x380">{dna.icon}</div>
            <div className="num-md wi-x381" style={{
              color:dna.color
            }}>{dna.class}</div>
            <div className="txt-prose wi-x382">{dna.desc}</div>
            <div className="wi-dna-stats">
              {[
                ['Total Trades', trades.length, 'var(--gold)'],
                ['Win Rate', Math.round(dna.wr * 100) + '%', dna.wr >= 0.5 ? 'var(--green)' : 'var(--red)'],
                ['Avg R:R', dna.avgRR.toFixed(1) + 'R', dna.color],
              ].map(([l, v, c]) => (
                <div key={l} className="wi-x383">
                  <div className="txt-micro txt-muted wi-x384">{l}</div>
                  <div className="num-sm" style={{ color:c }}>{v}</div>
                </div>
              ))}
            </div>
            <button onClick={() => setDnaOpen(false)} className="txt-btn wi-x385"
              style={{
                border:`1px solid ${dna.color}44`
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = dna.color; e.currentTarget.style.color = dna.color; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = `${dna.color}44`; e.currentTarget.style.color = 'var(--wi-text-550)'; }}>
              CLOSE
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   NAMED EXPORTS — teaser components and helpers that other pages may import
   for consistent locked-preview UI across the app.
───────────────────────────────────────────────────────────────────────────── */
export {
  CompoundProjectorTeaser,
  BestSetupFinderTeaser,
  TimingHeatmapTeaser,
  RiskSizingTeaser,
  UpgradeBanner,
  InsightCard,
  GoalTrackerTeaser,
};